import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useToast } from '../components/ToastContext';
import Toggle from '../components/Toggle';
import './InspectionWizard.css';

// Star Rating Component. `info` toont een ⓘ-icoontje dat op klik/tap (mobiel)
// of hover (desktop, via title) de uitleg laat zien.
const StarRating = ({ value, onChange, label, info }) => {
  const [hover, setHover] = useState(0);
  const [showInfo, setShowInfo] = useState(false);

  return (
    <div style={{ marginBottom: '0.4rem', padding: '0.35rem 0', borderBottom: '1px solid #f0f2f5' }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        alignItems: 'center',
        gap: '0.75rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', minWidth: 0 }}>
          <span style={{ fontSize: '0.95rem', color: '#2c3e50' }}>{label}</span>
          {info && (
            <button
              type="button"
              onClick={() => setShowInfo(s => !s)}
              aria-label="Toelichting"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '18px',
                height: '18px',
                borderRadius: '50%',
                border: '1.5px solid #246834',
                background: showInfo ? '#246834' : '#fff',
                color: showInfo ? '#fff' : '#246834',
                fontSize: '11px',
                fontWeight: 'bold',
                cursor: 'pointer',
                padding: 0,
                fontFamily: 'serif',
                flexShrink: 0
              }}
            >
              i
            </button>
          )}
        </div>
        <div style={{ display: 'flex', gap: '0.2rem', fontSize: '1.35rem', flexShrink: 0 }}>
          {[1, 2, 3, 4, 5].map((star) => (
            <span
              key={star}
              onClick={() => onChange(star)}
              onMouseEnter={() => setHover(star)}
              onMouseLeave={() => setHover(0)}
              style={{
                cursor: 'pointer',
                color: star <= (hover || value) ? '#ffc107' : '#ddd',
                transition: 'color 0.2s',
                userSelect: 'none',
                lineHeight: 1
              }}
            >
              ★
            </span>
          ))}
        </div>
      </div>
      {info && showInfo && (
        <p style={{ fontSize: '0.8rem', color: '#246834', margin: '0.4rem 0 0 0', lineHeight: '1.4', background: '#f0f7f1', padding: '0.4rem 0.6rem', borderRadius: '6px', border: '1px solid #c8e6c9' }}>
          {info}
        </p>
      )}
    </div>
  );
};

// Vakjargon-uitleg per veld (lekentaal). Wordt onder elk sterren-label getoond.
const FIELD_INFO = {
  // Houtwerk
  houtaantasting: 'Donkere plekken of zachte stukjes als je erop drukt — een teken dat het hout aan het rotten is.',
  openVerbindingen: 'Kieren waar het hout uit elkaar staat (bijvoorbeeld bij een hoek).',
  scheuren: 'Barsten of gaten in het hout.',
  klemmenDraaiendeDelen: 'Gaat het raam of de deur stroef of zwaar open?',
  scherpeKanten: 'Splinters of opstaand hout dat afgeschuurd moet worden.',
  houtvocht: 'Voelt het hout vochtig of nat aan?',
  tochtafdichting: 'Het rubber rondom dat de wind buiten houdt — zit dat nog goed vast?',
  scharnieren: 'Werken de scharnieren goed, of piepen/zakken ze?',
  sluitwerk: 'Slot, klink, kruk — gaat alles soepel?',
  // Schilderwerk
  glans: 'Glimt de verf nog, of is hij dof geworden?',
  verkleuring: 'Is de kleur veranderd of vergeeld?',
  verfschade: 'Plekken waar de verf afgebladderd is.',
  hechting: 'Laat de verf los? Kun je hem wegkrabben met je nagel?',
  regenschade: 'Strepen of afgespoelde plekken door regen.',
  slechtBesnijwerk: 'Is de afwerking onnetjes (oude schilderfouten)?',
  heiligeDagen: 'Stukjes die de schilder is vergeten te verven.',
  insluitingen: 'Bobbeltjes onder de verf (luchtbellen, stof, vuil).',
  zakkers: 'Verfdruppels die naar beneden zijn gezakt voordat ze droog waren.',
  // Glas
  gebrekenIsolatie: 'Vocht of condens tussen het dubbele glas — een teken dat de isolatie kapot is.',
  glasschade: 'Barst, kras of hagelschade in het glas.',
  gebrekenVentilatiekleppen: 'De roostertjes boven of onder het raam — werken die nog?',
  gebrekenGlasafdichting: 'De kit rondom het glas — zit die nog goed of laat die los?',
  gebrekenGlasbevestiging: 'Zit het glas nog stevig vast in het kozijn?',
  // Beton
  afgedrukteLoseDelen: 'Losse stukken beton, of plekken waar je beton af kunt drukken.',
  corrosieWapening: 'Roestende ijzeren stangen ín het beton — zichtbaar als oranje strepen of bobbels.',
  chloriden: 'Aantasting door strooizout — witte/zoute plekjes.',
  gietgallen: 'Luchtbellen of holtes in het beton (van vroeger, bij het storten).',
  oranjeAanslag: 'Roest-uitslag aan de buitenkant.',
  groeneAanslag: 'Algen of mos.',
  // Balustrade
  stevigheid: 'Rammelt de balustrade als je ertegen duwt?',
  duivenpinnen: 'De stekeltjes om duiven weg te houden — zitten die er nog en heel?'
};

// Helper: pak laatste segment uit een fieldPath als sleutel voor FIELD_INFO.
const fieldInfoFor = (fieldPath) => {
  if (!fieldPath) return undefined;
  const key = fieldPath.split('.').pop();
  return FIELD_INFO[key];
};

// Tweetraps-modus per inspectieblok. Persisteerd in localStorage per (token, sectionKey)
// zodat de keuze niet verloren gaat bij refresh. Mode-waarden:
//   null         → voorvraag tonen (twee knoppen)
//   'snelle'     → "alles in orde" — alleen overzichtsfoto + opmerking
//   'uitgebreide' → detailvragen ontvouwen
const useInspectionMode = (token, sectionKey) => {
  const storageKey = `opnamestaat-mode-${token}-${sectionKey}`;
  const [mode, setModeState] = useState(() => {
    try { return localStorage.getItem(storageKey) || null; } catch { return null; }
  });
  // Sync wanneer sectionKey verandert (Ramen-per-kamer wisselt dynamisch).
  useEffect(() => {
    try { setModeState(localStorage.getItem(storageKey) || null); } catch {}
  }, [storageKey]);
  const setMode = (next) => {
    setModeState(next);
    try {
      if (next == null) localStorage.removeItem(storageKey);
      else localStorage.setItem(storageKey, next);
    } catch {}
  };
  return [mode, setMode];
};

// Voorvraag-kaart: tweetraps-keuze met selected-state. Knoppen blijven beide
// zichtbaar zodat de bewoner makkelijk kan terugschakelen.
const VoorvraagCard = ({ vraag, onSnel, onUitgebreid, hint, mode }) => {
  const buttonStyle = (isActive) => ({
    flex: '1 1 140px',
    padding: '0.75rem',
    background: isActive ? '#246834' : '#fff',
    color: isActive ? '#fff' : '#2c3e50',
    border: '2px solid #246834',
    borderRadius: '8px',
    fontSize: '0.95rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background 0.15s, color 0.15s'
  });
  return (
    <div style={{
      background: '#f8f9fa',
      borderRadius: '10px',
      padding: '0.85rem',
      marginBottom: '0.75rem',
      border: '1px solid #dee2e6'
    }}>
      <p style={{ fontSize: '1rem', fontWeight: '600', margin: '0 0 0.5rem 0', color: '#2c3e50' }}>
        {vraag}
      </p>
      {hint && (
        <p style={{ fontSize: '0.85rem', color: '#7f8c8d', margin: '0 0 0.6rem 0', lineHeight: '1.4' }}>
          {hint}
        </p>
      )}
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        <button type="button" onClick={onSnel} style={buttonStyle(mode === 'snelle')}>
          {mode === 'snelle' ? '✓ ' : ''}Alles in orde
        </button>
        <button type="button" onClick={onUitgebreid} style={buttonStyle(mode === 'uitgebreide')}>
          {mode === 'uitgebreide' ? '✓ ' : ''}Iets melden
        </button>
      </div>
    </div>
  );
};

// OverzichtsfotoBlock: foto-upload dat altijd op de basispagina staat.
// Foto landt in de bestaande fotos-array met category='overzicht'.
const OverzichtsfotoBlock = ({ session, updateField, token, toast, location, fotosPath, label, hint, detailLabel = 'overzicht' }) => {
  const [uploading, setUploading] = useState(false);
  const getByPath = (obj, path) => path.split('.').reduce((o, k) => (o == null ? undefined : o[k]), obj);
  const currentFotos = getByPath(session, fotosPath) || [];
  const overzichtFoto = currentFotos.find(f => f.category === 'overzicht');

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('photo', file);
    formData.append('huisnummer', session.huisnummer);
    formData.append('location', location);
    formData.append('detail', detailLabel);
    try {
      const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:6001';
      const response = await fetch(`${backendUrl}/api/inspections/session/${token}/photo`, { method: 'POST', body: formData });
      if (!response.ok) throw new Error('Upload failed');
      const data = await response.json();
      const newFoto = { filename: data.filename, url: data.url || `${backendUrl}/uploads/inspections/${data.filename}`, category: 'overzicht' };
      const others = currentFotos.filter(f => f.category !== 'overzicht');
      updateField(fotosPath, [...others, newFoto]);
    } catch (err) {
      toast?.error?.('Foto upload mislukt: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const inputId = `overzicht-${location}-${detailLabel}-input`;
  return (
    <div className="form-group">
      <label>{label || 'Overzichtsfoto'}</label>
      {hint && (
        <p style={{ fontSize: '0.9rem', color: '#7f8c8d', margin: '0.25rem 0 0.5rem 0' }}>{hint}</p>
      )}
      <input
        type="file"
        accept="image/*"
        capture="environment"
        onChange={(e) => { const f = e.target.files[0]; if (f) handleUpload(f); e.target.value = ''; }}
        style={{ display: 'none' }}
        id={inputId}
        disabled={uploading}
      />
      <button
        type="button"
        onClick={() => document.getElementById(inputId).click()}
        disabled={uploading}
        style={{
          width: '100%',
          padding: '1rem',
          background: overzichtFoto ? 'linear-gradient(135deg, #27ae60 0%, #229954 100%)' : 'linear-gradient(135deg, #246834 0%, #1e5e2c 100%)',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          fontSize: '1rem',
          fontWeight: '600',
          cursor: uploading ? 'not-allowed' : 'pointer'
        }}
      >
        {uploading ? 'Uploaden...' : overzichtFoto ? '✓ Foto gemaakt — klik om te vervangen' : 'Maak overzichtsfoto'}
      </button>
      {overzichtFoto && (
        <img
          src={overzichtFoto.driveDisplayUrl || overzichtFoto.url}
          alt="Overzicht"
          style={{ width: '100%', maxHeight: '200px', objectFit: 'contain', borderRadius: '8px', marginTop: '0.75rem', border: '2px solid #27ae60' }}
        />
      )}
    </div>
  );
};

// Snelle-check: één overzichtsfoto + ruimte voor een korte opmerking.
// Foto's landen in de bestaande fotos-array van het blok, gemarkeerd met category='overzicht'.
// Backend-schema ongemoeid.
const SnelleCheckBlock = ({
  session,
  updateField,
  token,
  toast,
  location,           // bijv. 'voordeur', 'balkonKlein', 'kantelkiepramen'
  fotosPath,          // bijv. 'voordeur.fotos' of 'kantelkiepramen.slaapkamer.fotos'
  opmerkingenPath,    // bijv. 'voordeur.opmerkingen'
  onSwitchToUitgebreid,
  detailLabel = 'overzicht'
}) => {
  const [uploading, setUploading] = useState(false);

  // Lees fotos via dot-notation pad
  const getByPath = (obj, path) => path.split('.').reduce((o, k) => (o == null ? undefined : o[k]), obj);
  const currentFotos = getByPath(session, fotosPath) || [];
  const overzichtFoto = currentFotos.find(f => f.category === 'overzicht');
  const currentOpmerking = getByPath(session, opmerkingenPath) || '';

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('photo', file);
    formData.append('huisnummer', session.huisnummer);
    formData.append('location', location);
    formData.append('detail', detailLabel);
    try {
      const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:6001';
      const response = await fetch(`${backendUrl}/api/inspections/session/${token}/photo`, {
        method: 'POST',
        body: formData
      });
      if (!response.ok) throw new Error('Upload failed');
      const data = await response.json();
      const newFoto = {
        filename: data.filename,
        url: data.url || `${backendUrl}/uploads/inspections/${data.filename}`,
        category: 'overzicht'
      };
      const others = currentFotos.filter(f => f.category !== 'overzicht');
      updateField(fotosPath, [...others, newFoto]);
    } catch (err) {
      toast?.error?.('Foto upload mislukt: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={{ background: '#f0f7f1', padding: '1.5rem', borderRadius: '12px', border: '1px solid #c8e6c9' }}>
      <p style={{ margin: '0 0 1rem 0', color: '#2c3e50' }}>
        Mooi — even één overzichtsfoto en je bent klaar met dit onderdeel.
      </p>

      <div className="form-group">
        <input
          type="file"
          accept="image/*"
          capture="environment"
          onChange={(e) => { const f = e.target.files[0]; if (f) handleUpload(f); e.target.value = ''; }}
          style={{ display: 'none' }}
          id={`snelle-${location}-${detailLabel}-input`}
          disabled={uploading}
        />
        <button
          type="button"
          onClick={() => document.getElementById(`snelle-${location}-${detailLabel}-input`).click()}
          disabled={uploading}
          style={{
            width: '100%',
            padding: '1rem',
            background: overzichtFoto
              ? 'linear-gradient(135deg, #27ae60 0%, #229954 100%)'
              : 'linear-gradient(135deg, #246834 0%, #1e5e2c 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: uploading ? 'not-allowed' : 'pointer'
          }}
        >
          {uploading ? 'Uploaden...' : overzichtFoto ? '✓ Foto gemaakt — klik om te vervangen' : 'Maak een overzichtsfoto'}
        </button>
        {overzichtFoto && (
          <img
            src={overzichtFoto.driveDisplayUrl || overzichtFoto.url}
            alt="Overzicht"
            style={{ width: '100%', maxHeight: '200px', objectFit: 'contain', borderRadius: '8px', marginTop: '1rem', border: '2px solid #27ae60' }}
          />
        )}
      </div>

      <div className="form-group" style={{ marginTop: '1rem' }}>
        <label>Korte opmerking <span style={{ fontWeight: 'normal', color: '#7f8c8d' }}>(optioneel)</span></label>
        <textarea
          value={currentOpmerking}
          onChange={(e) => updateField(opmerkingenPath, e.target.value)}
          className="form-input"
          rows="2"
          placeholder="Iets dat je toch even wilt noemen..."
        />
      </div>

      {onSwitchToUitgebreid && (
        <p style={{ marginTop: '1rem', fontSize: '0.9rem' }}>
          Toch iets te melden?{' '}
          <button
            type="button"
            onClick={onSwitchToUitgebreid}
            style={{ background: 'none', border: 'none', color: '#246834', textDecoration: 'underline', cursor: 'pointer', padding: 0, font: 'inherit' }}
          >
            Schakel naar uitgebreide check
          </button>
        </p>
      )}
    </div>
  );
};

// Helper function to check if a field is visible
const isFieldVisible = (stepConfigs, stepId, fieldPath) => {
  const stepConfig = stepConfigs.find(config => config.stepId === stepId);
  if (!stepConfig || !stepConfig.fields || stepConfig.fields.length === 0) {
    return true; // Default to visible if no config
  }

  const field = stepConfig.fields.find(f => f.fieldPath === fieldPath);
  if (!field) {
    return true; // Default to visible if field not in config
  }

  return field.visible;
};

// Field Wrapper Component for conditional rendering
const FieldWrapper = ({ stepId, fieldPath, stepConfigs, children }) => {
  if (!isFieldVisible(stepConfigs, stepId, fieldPath)) {
    return null; // Don't render if hidden
  }
  return children;
};

// Volgorde van de raam-substappen binnen de Ramen-wizardstap.
const RAAM_TYPES = ['slaapkamer', 'woonkamerEnkel', 'woonkamerDubbel'];

const InspectionWizard = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [stepConfigs, setStepConfigs] = useState([]);
  const [selectedRaamType, setSelectedRaamType] = useState(RAAM_TYPES[0]);
  const [voordeurSubstep, setVoordeurSubstep] = useState('triage'); // 'triage' | 'detail' | 'foto'
  const [voordeurDetailSection, setVoordeurDetailSection] = useState('hout'); // 'hout' | 'schilder' | 'glas'
  const [ramenSubstep, setRamenSubstep] = useState('triage'); // 'triage' | 'detail' | 'foto' (per kamer)
  const [balkonKleinSubstep, setBalkonKleinSubstep] = useState('triage'); // 'triage' | 'detail' | 'foto'
  const [balkonGrootSubstep, setBalkonGrootSubstep] = useState('triage'); // 'triage' | 'detail' | 'foto'
  const [balkonGrootDetailSection, setBalkonGrootDetailSection] = useState('hout'); // 'hout' | 'schilder' | 'glas' | 'beton' | 'balustrade' | 'spullen'
  const [balkonKleinDetailSection, setBalkonKleinDetailSection] = useState('hout'); // 'hout' | 'schilder' | 'glas' | 'beton' | 'balustrade'
  const [installatiesPage, setInstallatiesPage] = useState('gas'); // 'gas' | 'apparaten' | 'ventilatie'
  const [ventilatiePage, setVentilatiePage] = useState('lucht'); // 'lucht' | 'verwarming' | 'afvoer'

  // Initialize default values for all fields
  const initializeDefaultValues = (sessionData, configData) => {
    const updatedSession = { ...sessionData };

    configData.forEach(stepConfig => {
      if (!stepConfig.fields || stepConfig.fields.length === 0) return;

      stepConfig.fields.forEach(field => {
        const fieldPath = field.fieldPath;
        if (!fieldPath) return;

        // Split path like "voordeur.hout.houtaantasting" into parts
        const parts = fieldPath.split('.');
        let current = updatedSession;

        // Navigate/create nested structure
        for (let i = 0; i < parts.length - 1; i++) {
          if (!current[parts[i]]) {
            current[parts[i]] = {};
          }
          current = current[parts[i]];
        }

        // Set default value if field doesn't exist
        const lastPart = parts[parts.length - 1];
        if (current[lastPart] === undefined || current[lastPart] === null) {
          // Set default based on field type
          if (field.type === 'star-rating') {
            current[lastPart] = 5; // Default to 5 stars
          } else if (field.type === 'number-input') {
            current[lastPart] = 0;
          } else if (field.type === 'dropdown') {
            current[lastPart] = '';
          } else if (field.type === 'textarea') {
            current[lastPart] = '';
          } else {
            current[lastPart] = '';
          }
        }
      });
    });

    return updatedSession;
  };

  // Load session data and step configurations
  useEffect(() => {
    const loadData = async () => {
      try {
        const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:6001';

        // Load session data
        const sessionResponse = await fetch(`${backendUrl}/api/inspections/session/${token}`);

        if (!sessionResponse.ok) {
          if (sessionResponse.status === 404) {
            throw new Error('Deze link is ongeldig of verlopen');
          }
          if (sessionResponse.status === 410) {
            throw new Error('Deze link is verlopen');
          }
          throw new Error('Er is iets misgegaan bij het laden van de opnamestaat');
        }

        const sessionData = await sessionResponse.json();

        // Load step configurations
        const configResponse = await fetch(`${backendUrl}/api/inspections/step-config`);
        const configData = await configResponse.json();

        // Initialize default values for all fields
        const sessionWithDefaults = initializeDefaultValues(sessionData, configData);

        setSession(sessionWithDefaults);
        setStepConfigs(configData);
        setCurrentStep(sessionData.currentStep || 0);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    loadData();
  }, [token]);

  // Auto-save when session data changes
  useEffect(() => {
    if (!session || loading) return;

    const saveTimer = setTimeout(async () => {
      await saveProgress();
    }, 2000); // Auto-save after 2 seconds of inactivity

    return () => clearTimeout(saveTimer);
  }, [session]);

  // Scroll to top when step or raam-substap changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep, selectedRaamType]);

  const saveProgress = async () => {
    if (!session) return;

    try {
      setSaving(true);
      const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:6001';
      const response = await fetch(`${backendUrl}/api/inspections/session/${token}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          currentStep,
          ...session
        })
      });

      if (!response.ok) {
        throw new Error('Opslaan mislukt');
      }
    } catch (err) {
      console.error('Auto-save failed:', err);
    } finally {
      setSaving(false);
    }
  };

  const updateField = (path, value) => {
    setSession(prev => {
      // Deep clone to avoid mutation issues with nested objects/arrays
      const updated = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let current = updated;

      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }

      current[keys[keys.length - 1]] = value;
      return updated;
    });
  };

  // Page-to-field mappings voor stappen die in sub-pagina's gesplitst zijn
  const installatiesPageFields = {
    gas: ['gebruikersonderhoud.gasaansluiting', 'gebruikersonderhoud.gasaansluitingOpmerking'],
    apparaten: ['gebruikersonderhoud.kookplaat', 'gebruikersonderhoud.oven', 'gebruikersonderhoud.koelkast', 'gebruikersonderhoud.diepvries', 'gebruikersonderhoud.cooker'],
    ventilatie: ['gebruikersonderhoud.mechanischeAfvoer', 'gebruikersonderhoud.apparatuurOpmerkingen'],
    meterkast: ['algemeen.aantalGroepenMeterkast', 'gebruikersonderhoud.extraGroepen', 'gebruikersonderhoud.groepenOpmerking']
  };
  const ventilatiePageFields = {
    lucht: ['gebruikersonderhoud.luchtafvoerKeuken', 'gebruikersonderhoud.luchtafvoerBadkamer', 'gebruikersonderhoud.luchtafvoerWC', 'gebruikersonderhoud.afvoerOpmerkingen'],
    verwarming: ['gebruikersonderhoud.verwarmingWoonkamer1', 'gebruikersonderhoud.verwarmingWoonkamer2', 'gebruikersonderhoud.verwarmingKeuken', 'gebruikersonderhoud.verwarmingBorrelkamer', 'gebruikersonderhoud.verwarmingWerkkamer', 'gebruikersonderhoud.verwarmingSlaapkamer', 'gebruikersonderhoud.verwarmingBadkamer', 'gebruikersonderhoud.verwarmingOpmerking'],
    afvoer: ['gebruikersonderhoud.afvoerBadkamer', 'gebruikersonderhoud.afvoerKeuken', 'gebruikersonderhoud.afvoerWC', 'gebruikersonderhoud.afvoerOpmerking']
  };

  // Geeft array van page-id's terug die ten minste één zichtbaar veld bevatten
  const visiblePagesFor = (stepId, pageFieldsMap) => {
    const stepConfig = stepConfigs.find(c => c.stepId === stepId);
    return Object.keys(pageFieldsMap).filter(page => {
      if (!stepConfig?.fields || stepConfig.fields.length === 0) return true;
      return pageFieldsMap[page].some(fp => {
        const field = stepConfig.fields.find(f => f.fieldPath === fp);
        return !field || field.visible !== false;
      });
    });
  };

  const nextStep = async () => {
    await saveProgress();
    const currentDef = steps[currentStep];

    // Voordeur substep navigation (binnen het hoofdstuk)
    if (currentDef?.id === 'voordeur') {
      if (voordeurSubstep === 'detail') {
        if (voordeurDetailSection === 'hout') { setVoordeurDetailSection('schilder'); return; }
        if (voordeurDetailSection === 'schilder') { setVoordeurDetailSection('glas'); return; }
        if (voordeurDetailSection === 'glas') { setVoordeurSubstep('foto'); return; }
      }
      // foto: val door naar volgend hoofdstuk; reset substep voor herhaalbezoek
      if (voordeurSubstep === 'foto') {
        setVoordeurSubstep('triage');
        setVoordeurDetailSection('hout');
      }
    }

    if (currentDef?.id === 'ramen') {
      // Substep navigatie binnen huidige kamer
      if (ramenSubstep === 'detail') { setRamenSubstep('foto'); return; }
      if (ramenSubstep === 'foto') {
        // Volgende kamer of volgend hoofdstuk
        const idx = RAAM_TYPES.indexOf(selectedRaamType);
        if (idx < RAAM_TYPES.length - 1) {
          setSelectedRaamType(RAAM_TYPES[idx + 1]);
          // ramenSubstep wordt automatisch gereset naar 'triage' via useEffect in RamenStep
          return;
        }
        // Laatste kamer: val door naar volgend hoofdstuk
        setRamenSubstep('triage');
      }
    }

    if (currentDef?.id === 'balkonKlein') {
      if (balkonKleinSubstep === 'detail') {
        const sections = ['hout', 'schilder', 'glas', 'beton', 'balustrade'];
        const idx = sections.indexOf(balkonKleinDetailSection);
        if (idx < sections.length - 1) { setBalkonKleinDetailSection(sections[idx + 1]); return; }
        setBalkonKleinSubstep('foto');
        return;
      }
      if (balkonKleinSubstep === 'foto') {
        setBalkonKleinSubstep('triage');
      }
    }

    if (currentDef?.id === 'balkonGroot') {
      if (balkonGrootSubstep === 'detail') {
        const sections = ['hout', 'schilder', 'glas', 'beton', 'balustrade', 'spullen'];
        const idx = sections.indexOf(balkonGrootDetailSection);
        if (idx < sections.length - 1) { setBalkonGrootDetailSection(sections[idx + 1]); return; }
        setBalkonGrootSubstep('foto');
        return;
      }
      if (balkonGrootSubstep === 'foto') {
        setBalkonGrootSubstep('triage');
      }
    }

    if (currentDef?.id === 'gebruikersonderhoud1') {
      const visiblePages = visiblePagesFor('gebruikersonderhoud1', installatiesPageFields);
      const idx = visiblePages.indexOf(installatiesPage);
      if (idx >= 0 && idx < visiblePages.length - 1) { setInstallatiesPage(visiblePages[idx + 1]); return; }
      setInstallatiesPage(visiblePages[0] || 'gas');
    }

    if (currentDef?.id === 'gebruikersonderhoud3') {
      const visiblePages = visiblePagesFor('gebruikersonderhoud3', ventilatiePageFields);
      const idx = visiblePages.indexOf(ventilatiePage);
      if (idx >= 0 && idx < visiblePages.length - 1) { setVentilatiePage(visiblePages[idx + 1]); return; }
      setVentilatiePage(visiblePages[0] || 'lucht');
    }

    setCurrentStep(prev => {
      const next = Math.min(prev + 1, steps.length - 1);
      if (steps[next]?.id === 'ramen') {
        setSelectedRaamType(RAAM_TYPES[0]);
      }
      if (steps[next]?.id === 'gebruikersonderhoud1') {
        const vp = visiblePagesFor('gebruikersonderhoud1', installatiesPageFields);
        setInstallatiesPage(vp[0] || 'gas');
      }
      if (steps[next]?.id === 'gebruikersonderhoud3') {
        const vp = visiblePagesFor('gebruikersonderhoud3', ventilatiePageFields);
        setVentilatiePage(vp[0] || 'lucht');
      }
      return next;
    });
  };

  const prevStep = () => {
    const currentDef = steps[currentStep];

    // Voordeur substep navigation (binnen het hoofdstuk)
    if (currentDef?.id === 'voordeur') {
      if (voordeurSubstep === 'foto') {
        const storedMode = localStorage.getItem(`opnamestaat-mode-${token}-voordeur`);
        if (storedMode === 'uitgebreide') {
          setVoordeurSubstep('detail');
          setVoordeurDetailSection('glas');
        } else {
          setVoordeurSubstep('triage');
        }
        return;
      }
      if (voordeurSubstep === 'detail') {
        if (voordeurDetailSection === 'glas') { setVoordeurDetailSection('schilder'); return; }
        if (voordeurDetailSection === 'schilder') { setVoordeurDetailSection('hout'); return; }
        if (voordeurDetailSection === 'hout') { setVoordeurSubstep('triage'); return; }
      }
      // triage: val door naar vorig hoofdstuk
    }

    if (currentDef?.id === 'ramen') {
      // Substep navigatie binnen huidige kamer
      if (ramenSubstep === 'foto') {
        const storedMode = localStorage.getItem(`opnamestaat-mode-${token}-ramen-${selectedRaamType}`);
        if (storedMode === 'uitgebreide') { setRamenSubstep('detail'); return; }
        setRamenSubstep('triage');
        return;
      }
      if (ramenSubstep === 'detail') { setRamenSubstep('triage'); return; }
      if (ramenSubstep === 'triage') {
        // Vorige kamer of vorig hoofdstuk
        const idx = RAAM_TYPES.indexOf(selectedRaamType);
        if (idx > 0) {
          setSelectedRaamType(RAAM_TYPES[idx - 1]);
          // useEffect reset substep bij selectedRaamType-wisseling
          return;
        }
        // Eerste kamer: val door naar vorig hoofdstuk
      }
    }

    if (currentDef?.id === 'balkonKlein') {
      if (balkonKleinSubstep === 'foto') {
        const storedMode = localStorage.getItem(`opnamestaat-mode-${token}-balkonKlein`);
        if (storedMode === 'uitgebreide') {
          setBalkonKleinSubstep('detail');
          setBalkonKleinDetailSection('balustrade');
          return;
        }
        setBalkonKleinSubstep('triage');
        return;
      }
      if (balkonKleinSubstep === 'detail') {
        const sections = ['hout', 'schilder', 'glas', 'beton', 'balustrade'];
        const idx = sections.indexOf(balkonKleinDetailSection);
        if (idx > 0) { setBalkonKleinDetailSection(sections[idx - 1]); return; }
        setBalkonKleinSubstep('triage');
        return;
      }
      // triage: val door naar vorig hoofdstuk
    }

    if (currentDef?.id === 'balkonGroot') {
      if (balkonGrootSubstep === 'foto') {
        const storedMode = localStorage.getItem(`opnamestaat-mode-${token}-balkonGroot`);
        if (storedMode === 'uitgebreide') {
          setBalkonGrootSubstep('detail');
          setBalkonGrootDetailSection('spullen');
          return;
        }
        setBalkonGrootSubstep('triage');
        return;
      }
      if (balkonGrootSubstep === 'detail') {
        const sections = ['hout', 'schilder', 'glas', 'beton', 'balustrade', 'spullen'];
        const idx = sections.indexOf(balkonGrootDetailSection);
        if (idx > 0) { setBalkonGrootDetailSection(sections[idx - 1]); return; }
        setBalkonGrootSubstep('triage');
        return;
      }
      // triage: val door naar vorig hoofdstuk
    }

    if (currentDef?.id === 'gebruikersonderhoud1') {
      const visiblePages = visiblePagesFor('gebruikersonderhoud1', installatiesPageFields);
      const idx = visiblePages.indexOf(installatiesPage);
      if (idx > 0) { setInstallatiesPage(visiblePages[idx - 1]); return; }
      // eerste page: val door naar vorig hoofdstuk
    }

    if (currentDef?.id === 'gebruikersonderhoud3') {
      const visiblePages = visiblePagesFor('gebruikersonderhoud3', ventilatiePageFields);
      const idx = visiblePages.indexOf(ventilatiePage);
      if (idx > 0) { setVentilatiePage(visiblePages[idx - 1]); return; }
      // eerste page: val door naar vorig hoofdstuk
    }

    setCurrentStep(prev => {
      const next = Math.max(prev - 1, 0);
      if (steps[next]?.id === 'ramen') {
        setSelectedRaamType(RAAM_TYPES[RAAM_TYPES.length - 1]);
      }
      if (steps[next]?.id === 'gebruikersonderhoud1') {
        const vp = visiblePagesFor('gebruikersonderhoud1', installatiesPageFields);
        setInstallatiesPage(vp[vp.length - 1] || 'gas');
      }
      if (steps[next]?.id === 'gebruikersonderhoud3') {
        const vp = visiblePagesFor('gebruikersonderhoud3', ventilatiePageFields);
        setVentilatiePage(vp[vp.length - 1] || 'lucht');
      }
      return next;
    });
  };

  const submitInspection = async () => {
    try {
      setSaving(true);
      const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:6001';
      const response = await fetch(`${backendUrl}/api/inspections/session/${token}/submit`, {
        method: 'POST'
      });

      if (!response.ok) {
        throw new Error('Indienen mislukt');
      }

      const data = await response.json();
      toast.success(data.message);
      navigate('/'); // Redirect to home or thank you page
    } catch (err) {
      toast.error('Er is iets misgegaan bij het indienen: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Wizard steps configuration (all steps)
  const allSteps = [
    {
      id: 'welkom',
      title: 'Welkom',
      component: WelcomeStep
    },
    {
      id: 'toegang',
      title: 'Toegang & Sleutels',
      component: ToeganStep
    },
    {
      id: 'voordeur',
      title: 'Voordeur',
      component: VoordeurStep
    },
    {
      id: 'ramen',
      title: 'Ramen',
      component: RamenStep
    },
    {
      id: 'balkonKlein',
      title: 'Balkon Klein',
      component: BalkonKleinStep
    },
    {
      id: 'balkonGroot',
      title: 'Balkon Groot',
      component: BalkonGrootStep
    },
    {
      id: 'gebruikersonderhoud1',
      title: 'Installaties',
      component: GebruikersonderhoudApparatenStep
    },
    {
      id: 'gebruikersonderhoud2',
      title: 'Onderhoud: Aansluitingen',
      component: GebruikersonderhoudAansluitingenStep
    },
    {
      id: 'gebruikersonderhoud3',
      title: 'Ventilatie, verwarming & afvoer',
      component: GebruikersonderhoudVentilatieVerwarmingStep
    },
    {
      id: 'gebruikersonderhoud4',
      title: 'Onderhoud: Afvoer & Foto\'s',
      component: GebruikersonderhoudAfvoerFotosStep
    },
    {
      id: 'gebruikersonderhoud5',
      title: 'Opmerkingen & foto\'s',
      component: GebruikersonderhoudAanpassingenStep
    },
    {
      id: 'algemeen',
      title: 'Algemene vragen',
      component: AlgemeenStep
    },
    {
      id: 'review',
      title: 'Controleren & Indienen',
      component: ReviewStep
    }
  ];

  // Filter steps based on visibility configuration
  const steps = stepConfigs.length > 0
    ? allSteps.filter(step => {
        const config = stepConfigs.find(c => c.stepId === step.id);
        return config ? config.visible : true;
      })
    : allSteps;

  if (loading) {
    return (
      <div className="inspection-wizard loading">
        <div className="spinner"></div>
        <p>Opnamestaat laden...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="inspection-wizard error">
        <div className="error-card">
          <h2>Fout</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/')}>Terug naar home</button>
        </div>
      </div>
    );
  }

  const CurrentStepComponent = steps[currentStep].component;

  return (
    <div className="inspection-wizard">
      {/* Header */}
      <div className="wizard-header">
        <h1>Onderhoudscheck</h1>
        <span className="wizard-subtitle">• {session.huisnummer}</span>
        {saving && <span className="save-indicator">Opslaan...</span>}
      </div>

      {/* Progress Bar */}
      <div className="progress-container">
        <div className="progress-bar">
          <div
            className="progress-fill"
            style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
          ></div>
        </div>
        <p className="progress-text">
          Stap {currentStep + 1} van {steps.length}: {steps[currentStep].title}
        </p>
      </div>

      {/* Step Content */}
      <div className="wizard-content">
        <CurrentStepComponent
          session={session}
          updateField={updateField}
          token={token}
          stepConfigs={stepConfigs}
          toast={toast}
          selectedRaamType={selectedRaamType}
          setSelectedRaamType={setSelectedRaamType}
          voordeurSubstep={voordeurSubstep}
          setVoordeurSubstep={setVoordeurSubstep}
          voordeurDetailSection={voordeurDetailSection}
          setVoordeurDetailSection={setVoordeurDetailSection}
          ramenSubstep={ramenSubstep}
          setRamenSubstep={setRamenSubstep}
          balkonKleinSubstep={balkonKleinSubstep}
          setBalkonKleinSubstep={setBalkonKleinSubstep}
          balkonGrootSubstep={balkonGrootSubstep}
          setBalkonGrootSubstep={setBalkonGrootSubstep}
          balkonGrootDetailSection={balkonGrootDetailSection}
          setBalkonGrootDetailSection={setBalkonGrootDetailSection}
          balkonKleinDetailSection={balkonKleinDetailSection}
          setBalkonKleinDetailSection={setBalkonKleinDetailSection}
          installatiesPage={installatiesPage}
          setInstallatiesPage={setInstallatiesPage}
          ventilatiePage={ventilatiePage}
          setVentilatiePage={setVentilatiePage}
        />
      </div>

      {/* Navigation */}
      <div className="wizard-navigation">
        {currentStep > 0 && (
          <button
            onClick={prevStep}
            className="btn btn-secondary"
          >
            ← Vorige
          </button>
        )}

        {(steps[currentStep]?.id === 'voordeur' && voordeurSubstep === 'triage')
          || (steps[currentStep]?.id === 'ramen' && ramenSubstep === 'triage')
          || (steps[currentStep]?.id === 'balkonKlein' && balkonKleinSubstep === 'triage')
          || (steps[currentStep]?.id === 'balkonGroot' && balkonGrootSubstep === 'triage') ? (
          null
        ) : currentStep < steps.length - 1 ? (
          <button
            onClick={nextStep}
            className="btn btn-primary"
          >
            Volgende →
          </button>
        ) : (
          <button
            onClick={submitInspection}
            className="btn btn-success"
            disabled={saving}
          >
            {saving ? 'Indienen...' : 'Onderhoudscheck indienen'}
          </button>
        )}
      </div>
    </div>
  );
};

// Step Components
const WelcomeStep = ({ session }) => (
  <div className="step-content">
    <h2>Welkom — {session.huisnummer}</h2>
    {session.bewoner?.naam && <p style={{ color: '#7f8c8d' }}>{session.bewoner.naam}</p>}

    <div className="info-box">
      <h3>Hoe het werkt</h3>
      <p>
        Je loopt met je telefoon door je huis en beantwoordt onderweg een paar vragen per onderdeel.
        Bij elk hoofdstuk kun je kiezen: <em>alles in orde</em> (dan ben je snel klaar), of <em>ik
        wil iets melden</em> (dan stelt de app gerichte vervolgvragen).
      </p>
      <p>
        Reken op een <strong>kwartier</strong> voor de meeste woningen, een <strong>half uur</strong>
        als er veel te melden is. <strong>Tussentijds opslaan kan altijd</strong> — je kunt 'm dus
        rustig in stukjes doen.
      </p>
    </div>

    <div className="info-box">
      <h3>Jij houdt de regie</h3>
      <p>
        Jij vult in wat jij relevant vindt en maakt foto's van wat jij wilt laten zien. Geen
        rondleiding door je woonkamer, geen vreemde die in je kasten kijkt. Snap je een term niet?
        Bij elke vraag staat een korte uitleg.
      </p>
    </div>
  </div>
);

const ToeganStep = ({ session, updateField, stepConfigs }) => (
  <div className="step-content">
    <h2>Toegang & Sleutels</h2>
    <p className="step-description">Vul hieronder in hoeveel sleutels en toegangstags u heeft ontvangen.</p>

    <FieldWrapper stepId="toegang" fieldPath="toegang.gebouw" stepConfigs={stepConfigs}>
      <div className="form-group">
        <label>Aantal sleutels gebouw</label>
        <input
          type="number"
          min="0"
          value={session.toegang?.gebouw || ''}
          onChange={(e) => updateField('toegang.gebouw', parseInt(e.target.value) || 0)}
          className="form-input"
        />
      </div>
    </FieldWrapper>

    <FieldWrapper stepId="toegang" fieldPath="toegang.tussendeurTag" stepConfigs={stepConfigs}>
      <div className="form-group">
        <label>Aantal toegangstags tussendeur</label>
        <input
          type="number"
          min="0"
          value={session.toegang?.tussendeurTag || ''}
          onChange={(e) => updateField('toegang.tussendeurTag', parseInt(e.target.value) || 0)}
          className="form-input"
        />
      </div>
    </FieldWrapper>

    <FieldWrapper stepId="toegang" fieldPath="toegang.appartement" stepConfigs={stepConfigs}>
      <div className="form-group">
        <label>Aantal sleutels appartement</label>
        <input
          type="number"
          min="0"
          value={session.toegang?.appartement || ''}
          onChange={(e) => updateField('toegang.appartement', parseInt(e.target.value) || 0)}
          className="form-input"
        />
      </div>
    </FieldWrapper>

    <FieldWrapper stepId="toegang" fieldPath="toegang.berging" stepConfigs={stepConfigs}>
      <div className="form-group">
        <label>Aantal sleutels berging</label>
        <input
          type="number"
          min="0"
          value={session.toegang?.berging || ''}
          onChange={(e) => updateField('toegang.berging', parseInt(e.target.value) || 0)}
          className="form-input"
        />
      </div>
    </FieldWrapper>
  </div>
);

const VoordeurStep = ({ session, updateField, token, toast, stepConfigs, voordeurSubstep: substep, setVoordeurSubstep: setSubstep, voordeurDetailSection: detailSection, setVoordeurDetailSection: setDetailSection }) => {
  const [uploading, setUploading] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [mode, setMode] = useInspectionMode(token, 'voordeur');

  // Helper function to check if a field is visible
  const isFieldVisible = (fieldPath) => {
    const stepConfig = stepConfigs?.find(config => config.stepId === 'voordeur');
    if (!stepConfig || !stepConfig.fields || stepConfig.fields.length === 0) {
      return true; // Default to visible if no config
    }
    const field = stepConfig.fields.find(f => f.fieldPath === fieldPath);
    if (!field) {
      return true; // Default to visible if field not in config
    }
    return field.visible !== false;
  };

  const handlePhotoSelect = (e, category) => {
    const file = e.target.files[0];
    if (!file) return;

    // Show preview immediately
    const reader = new FileReader();
    reader.onloadend = () => {
      setPhotoPreview(reader.result);
      setSelectedFile(file);
      setSelectedCategory(category);
    };
    reader.readAsDataURL(file);

    // Clear the input so same file can be selected again
    e.target.value = '';
  };

  const confirmPhoto = async () => {
    if (!selectedFile || !selectedCategory) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('photo', selectedFile);
    formData.append('huisnummer', session.huisnummer);
    formData.append('location', 'voordeur');
    formData.append('detail', selectedCategory);

    try {
      const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:6001';
      const response = await fetch(`${backendUrl}/api/inspections/session/${token}/photo`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) throw new Error('Upload failed');

      const data = await response.json();

      // Add photo to session
      const currentPhotos = session.voordeur?.fotos || [];
      updateField('voordeur.fotos', [...currentPhotos, {
        filename: data.filename,
        url: data.url || `${backendUrl}/uploads/inspections/${data.filename}`,
        category: selectedCategory
      }]);

      // Clear preview and selection
      setPhotoPreview(null);
      setSelectedFile(null);
      setSelectedCategory(null);
    } catch (err) {
      toast.error('Foto upload mislukt: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const cancelPhoto = () => {
    setPhotoPreview(null);
    setSelectedFile(null);
    setSelectedCategory(null);
  };

  const deletePhoto = (index) => {
    const currentPhotos = session.voordeur?.fotos || [];
    const newPhotos = currentPhotos.filter((_, idx) => idx !== index);
    updateField('voordeur.fotos', newPhotos);
  };

  return (
    <div className="step-content">
      {substep === 'triage' && (
        <>
          <h2 style={{ marginTop: 0, marginBottom: '0.6rem', fontSize: '1.3rem' }}>De voordeur</h2>

          <VoorvraagCard
            vraag="Hoe staat de voordeur erbij?"
            hint="Krasjes bij het slot, een geraakt hoekje, schade aan de rode verf — dat soort dingen zie jij."
            mode={mode}
            onSnel={() => { setMode('snelle'); setSubstep('foto'); }}
            onUitgebreid={() => { setMode('uitgebreide'); setSubstep('detail'); setDetailSection('hout'); }}
          />

          <details style={{ marginTop: '1rem', fontSize: '0.85rem', color: '#7f8c8d' }}>
            <summary style={{ cursor: 'pointer' }}>Wat bekijk je?</summary>
            <ul style={{ margin: '0.4rem 0 0 0', paddingLeft: '1.2rem' }}>
              <li>Het hout en schilderwerk (de rode verf)</li>
              <li>Sleutel-schade rond het slot</li>
              <li>Het kozijn naast de deur</li>
              <li>Het raampje (als je er een hebt)</li>
            </ul>
          </details>
        </>
      )}

      {substep === 'foto' && (
        <>
          <h2>De voordeur — foto en toelichting</h2>

          <OverzichtsfotoBlock
            session={session}
            updateField={updateField}
            token={token}
            toast={toast}
            location="voordeur"
            fotosPath="voordeur.fotos"
            label="Overzichtsfoto van de voordeur"
            hint="Eén foto van de hele voordeur, vanaf de gang of het portiek."
          />

          {isFieldVisible('voordeur.opmerkingen') && (
            <div className="form-group">
              <label>Korte opmerking <span style={{ fontWeight: 'normal', color: '#7f8c8d' }}>(optioneel)</span></label>
              <textarea
                value={session.voordeur?.opmerkingen || ''}
                onChange={(e) => updateField('voordeur.opmerkingen', e.target.value)}
                className="form-input"
                rows="2"
                placeholder="Iets dat je toch even wilt noemen..."
              />
            </div>
          )}
        </>
      )}

      {substep === 'detail' && (
      <>
      <details style={{ marginBottom: '0.75rem', fontSize: '0.85rem', color: '#7f8c8d' }}>
        <summary style={{ cursor: 'pointer' }}>Hoe werkt de sterren-beoordeling?</summary>
        <p style={{ margin: '0.4rem 0 0 0' }}>
          <strong>5</strong> uitstekend, <strong>4</strong> goed, <strong>3</strong> redelijk, <strong>2</strong> matig, <strong>1</strong> slecht.
          Klik op de <strong>i</strong>-knop voor uitleg per onderdeel. Twijfel? Kies de laagste.
        </p>
      </details>

      {detailSection === 'hout' && (
      <>
      <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#2c3e50' }}>Houtwerk</h3>

      {isFieldVisible('voordeur.hout.houtaantasting') && (
        <StarRating
          label="Houtaantasting"
          value={session.voordeur?.hout?.houtaantasting || 5}
          onChange={(value) => updateField('voordeur.hout.houtaantasting', value)}
          info={fieldInfoFor('voordeur.hout.houtaantasting')}
        />
      )}

      {isFieldVisible('voordeur.hout.openVerbindingen') && (
        <StarRating
          label="Open verbindingen"
          value={session.voordeur?.hout?.openVerbindingen || 5}
          onChange={(value) => updateField('voordeur.hout.openVerbindingen', value)}
          info={fieldInfoFor('voordeur.hout.openVerbindingen')}
        />
      )}

      {isFieldVisible('voordeur.hout.scheuren') && (
        <StarRating
          label="Scheuren"
          value={session.voordeur?.hout?.scheuren || 5}
          onChange={(value) => updateField('voordeur.hout.scheuren', value)}
          info={fieldInfoFor('voordeur.hout.scheuren')}
        />
      )}

      {isFieldVisible('voordeur.hout.klemmenDraaiendeDelen') && (
        <StarRating
          label="Klemmen draaiende delen"
          value={session.voordeur?.hout?.klemmenDraaiendeDelen || 5}
          onChange={(value) => updateField('voordeur.hout.klemmenDraaiendeDelen', value)}
          info={fieldInfoFor('voordeur.hout.klemmenDraaiendeDelen')}
        />
      )}

      {isFieldVisible('voordeur.hout.scherpeKanten') && (
        <StarRating
          label="Scherpe kanten"
          value={session.voordeur?.hout?.scherpeKanten || 5}
          onChange={(value) => updateField('voordeur.hout.scherpeKanten', value)}
          info={fieldInfoFor('voordeur.hout.scherpeKanten')}
        />
      )}

      {isFieldVisible('voordeur.hout.houtvocht') && (
        <StarRating
          label="Houtvocht"
          value={session.voordeur?.hout?.houtvocht || 5}
          onChange={(value) => updateField('voordeur.hout.houtvocht', value)}
          info={fieldInfoFor('voordeur.hout.houtvocht')}
        />
      )}

      {isFieldVisible('voordeur.hout.tochtafdichting') && (
        <StarRating
          label="Tochtafdichting"
          value={session.voordeur?.hout?.tochtafdichting || 5}
          onChange={(value) => updateField('voordeur.hout.tochtafdichting', value)}
          info={fieldInfoFor('voordeur.hout.tochtafdichting')}
        />
      )}

      {isFieldVisible('voordeur.hout.scharnieren') && (
        <StarRating
          label="Scharnieren"
          value={session.voordeur?.hout?.scharnieren || 5}
          onChange={(value) => updateField('voordeur.hout.scharnieren', value)}
          info={fieldInfoFor('voordeur.hout.scharnieren')}
        />
      )}

      {isFieldVisible('voordeur.hout.sluitwerk') && (
        <StarRating
          label="Sluitwerk"
          value={session.voordeur?.hout?.sluitwerk || 5}
          onChange={(value) => updateField('voordeur.hout.sluitwerk', value)}
          info={fieldInfoFor('voordeur.hout.sluitwerk')}
        />
      )}
      </>
      )}

      {detailSection === 'schilder' && (
      <>
      <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#2c3e50' }}>Schilderwerk</h3>

      {isFieldVisible('voordeur.schilderwerk.glans') && (
        <StarRating
          label="Glans"
          value={session.voordeur?.schilderwerk?.glans || 5}
          onChange={(value) => updateField('voordeur.schilderwerk.glans', value)}
          info={fieldInfoFor('voordeur.schilderwerk.glans')}
        />
      )}

      {isFieldVisible('voordeur.schilderwerk.verkleuring') && (
        <StarRating
          label="Verkleuring"
          value={session.voordeur?.schilderwerk?.verkleuring || 5}
          onChange={(value) => updateField('voordeur.schilderwerk.verkleuring', value)}
          info={fieldInfoFor('voordeur.schilderwerk.verkleuring')}
        />
      )}

      {isFieldVisible('voordeur.schilderwerk.verfschade') && (
        <StarRating
          label="Verfschade"
          value={session.voordeur?.schilderwerk?.verfschade || 5}
          onChange={(value) => updateField('voordeur.schilderwerk.verfschade', value)}
          info={fieldInfoFor('voordeur.schilderwerk.verfschade')}
        />
      )}

      {isFieldVisible('voordeur.schilderwerk.hechting') && (
        <StarRating
          label="Hechting"
          value={session.voordeur?.schilderwerk?.hechting || 5}
          onChange={(value) => updateField('voordeur.schilderwerk.hechting', value)}
          info={fieldInfoFor('voordeur.schilderwerk.hechting')}
        />
      )}

      {isFieldVisible('voordeur.schilderwerk.regenschade') && (
        <StarRating
          label="Regenschade"
          value={session.voordeur?.schilderwerk?.regenschade || 5}
          onChange={(value) => updateField('voordeur.schilderwerk.regenschade', value)}
          info={fieldInfoFor('voordeur.schilderwerk.regenschade')}
        />
      )}

      {isFieldVisible('voordeur.schilderwerk.slechtBesnijwerk') && (
        <StarRating
          label="Slecht besnijwerk"
          value={session.voordeur?.schilderwerk?.slechtBesnijwerk || 5}
          onChange={(value) => updateField('voordeur.schilderwerk.slechtBesnijwerk', value)}
          info={fieldInfoFor('voordeur.schilderwerk.slechtBesnijwerk')}
        />
      )}

      {isFieldVisible('voordeur.schilderwerk.heiligeDagen') && (
        <StarRating
          label="Heilige dagen"
          value={session.voordeur?.schilderwerk?.heiligeDagen || 5}
          onChange={(value) => updateField('voordeur.schilderwerk.heiligeDagen', value)}
          info={fieldInfoFor('voordeur.schilderwerk.heiligeDagen')}
        />
      )}

      {isFieldVisible('voordeur.schilderwerk.insluitingen') && (
        <StarRating
          label="Insluitingen"
          value={session.voordeur?.schilderwerk?.insluitingen || 5}
          onChange={(value) => updateField('voordeur.schilderwerk.insluitingen', value)}
          info={fieldInfoFor('voordeur.schilderwerk.insluitingen')}
        />
      )}

      {isFieldVisible('voordeur.schilderwerk.zakkers') && (
        <StarRating
          label="Zakkers"
          value={session.voordeur?.schilderwerk?.zakkers || 5}
          onChange={(value) => updateField('voordeur.schilderwerk.zakkers', value)}
          info={fieldInfoFor('voordeur.schilderwerk.zakkers')}
        />
      )}
      </>
      )}

      {detailSection === 'glas' && (
      <>
      <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#2c3e50' }}>Beglazing bij voordeur?</h3>

      {isFieldVisible('voordeur.glas.type') && (
        <div className="form-group">
          <select
            value={session.voordeur?.glas?.type || ''}
            onChange={(e) => updateField('voordeur.glas.type', e.target.value)}
            className="form-input"
          >
            <option value="">Kies...</option>
            <option value="hout">Hout (geen glas)</option>
            <option value="draadglas">Draadglas</option>
            <option value="anders">Anders</option>
          </select>
        </div>
      )}

      {isFieldVisible('voordeur.glas.gebrekenIsolatie') && session.voordeur?.glas?.type && session.voordeur?.glas?.type !== 'hout' && (
        <StarRating
          label="Gebreken isolatie"
          value={session.voordeur?.glas?.gebrekenIsolatie || 5}
          onChange={(value) => updateField('voordeur.glas.gebrekenIsolatie', value)}
          info={fieldInfoFor('voordeur.glas.gebrekenIsolatie')}
        />
      )}

      {isFieldVisible('voordeur.glas.glasschade') && session.voordeur?.glas?.type && session.voordeur?.glas?.type !== 'hout' && (
        <StarRating
          label="Glasschade"
          value={session.voordeur?.glas?.glasschade || 5}
          onChange={(value) => updateField('voordeur.glas.glasschade', value)}
          info={fieldInfoFor('voordeur.glas.glasschade')}
        />
      )}

      {isFieldVisible('voordeur.glas.gebrekenVentilatiekleppen') && session.voordeur?.glas?.type && session.voordeur?.glas?.type !== 'hout' && (
        <StarRating
          label="Gebreken ventilatiekleppen"
          value={session.voordeur?.glas?.gebrekenVentilatiekleppen || 5}
          onChange={(value) => updateField('voordeur.glas.gebrekenVentilatiekleppen', value)}
          info={fieldInfoFor('voordeur.glas.gebrekenVentilatiekleppen')}
        />
      )}

      {isFieldVisible('voordeur.glas.gebrekenGlasafdichting') && session.voordeur?.glas?.type && session.voordeur?.glas?.type !== 'hout' && (
        <StarRating
          label="Gebreken glasafdichting"
          value={session.voordeur?.glas?.gebrekenGlasafdichting || 5}
          onChange={(value) => updateField('voordeur.glas.gebrekenGlasafdichting', value)}
          info={fieldInfoFor('voordeur.glas.gebrekenGlasafdichting')}
        />
      )}

      {isFieldVisible('voordeur.glas.gebrekenGlasbevestiging') && session.voordeur?.glas?.type && session.voordeur?.glas?.type !== 'hout' && (
        <StarRating
          label="Gebreken glasbevestiging"
          value={session.voordeur?.glas?.gebrekenGlasbevestiging || 5}
          onChange={(value) => updateField('voordeur.glas.gebrekenGlasbevestiging', value)}
          info={fieldInfoFor('voordeur.glas.gebrekenGlasbevestiging')}
        />
      )}
      </>
      )}
      </>
      )}

      {substep === 'foto' && mode === 'uitgebreide' && (
      <>
      {!photoPreview && (
        <div className="form-group" style={{ marginTop: '2rem' }}>
          <label>Detailfoto's van gebreken</label>
          <p style={{ fontSize: '0.9rem', color: '#7f8c8d', marginBottom: '0.5rem' }}>
            Maak foto's van specifieke gebreken (meerdere foto's mogelijk)
          </p>
          <input
            type="file"
            accept="image/*"
            capture="environment"
            onChange={(e) => handlePhotoSelect(e, 'detail')}
            style={{ display: 'none' }}
            id="detail-input"
            disabled={uploading}
          />
          <button
            type="button"
            onClick={() => document.getElementById('detail-input').click()}
            disabled={uploading}
            style={{
              width: '100%',
              padding: '1rem',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: uploading ? 'not-allowed' : 'pointer',
              boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)'
            }}
          >
            Maak foto
          </button>
        </div>
      )}

      {photoPreview && (
        <div style={{ marginTop: '1rem', padding: '1rem', background: '#f8f9fa', borderRadius: '8px' }}>
          <img
            src={photoPreview}
            alt="Preview"
            style={{
              width: '100%',
              maxHeight: '250px',
              objectFit: 'contain',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              marginBottom: '1rem'
            }}
          />
          <div style={{ display: 'flex', gap: '1rem' }}>
            <button
              onClick={confirmPhoto}
              disabled={uploading}
              style={{
                flex: 1,
                padding: '0.75rem',
                background: '#27ae60',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: uploading ? 'not-allowed' : 'pointer'
              }}
            >
              {uploading ? 'Uploaden...' : 'Bevestigen'}
            </button>
            <button
              onClick={cancelPhoto}
              disabled={uploading}
              style={{
                flex: 1,
                padding: '0.75rem',
                background: '#e74c3c',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: uploading ? 'not-allowed' : 'pointer'
              }}
            >
              Verwijderen
            </button>
          </div>
        </div>
      )}

      {session.voordeur?.fotos && session.voordeur.fotos.length > 0 && (
        <div style={{ marginTop: '1.5rem' }}>
          <h3 style={{ marginBottom: '1rem', color: '#2c3e50' }}>Geüploade foto's ({session.voordeur.fotos.length}):</h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
            gap: '1rem'
          }}>
            {session.voordeur.fotos.map((foto, idx) => {
              const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:6001';
              const imageUrl = typeof foto === 'string'
                ? `${backendUrl}/uploads/inspections/${foto}`
                : foto.driveDisplayUrl || foto.url || `${backendUrl}/uploads/inspections/${foto.filename}`;

              // Extract category from foto object or filename
              let category = 'foto';
              if (typeof foto === 'object' && foto.category) {
                category = foto.category;
              } else if (typeof foto === 'string') {
                // Try to extract from filename: "TEST-201 voordeur detail.jpeg"
                if (foto.includes('overzicht')) category = 'overzicht';
                else if (foto.includes('detail')) category = 'detail';
              } else if (foto.filename) {
                // Try to extract from filename in object
                if (foto.filename.includes('overzicht')) category = 'overzicht';
                else if (foto.filename.includes('detail')) category = 'detail';
              }

              return (
                <div key={idx} style={{
                  position: 'relative',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}>
                  <img
                    src={imageUrl}
                    alt={`Voordeur ${category} ${idx + 1}`}
                    style={{
                      width: '100%',
                      height: '150px',
                      objectFit: 'contain',
                      backgroundColor: '#f8f9fa'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'block';
                    }}
                  />
                  <div style={{
                    display: 'none',
                    padding: '1rem',
                    backgroundColor: '#f0f0f0',
                    textAlign: 'center',
                    fontSize: '0.8rem'
                  }}>
                    {typeof foto === 'string' ? foto : foto.filename}
                  </div>
                  <button
                    onClick={() => deletePhoto(idx)}
                    style={{
                      position: 'absolute',
                      top: '0.5rem',
                      right: '0.5rem',
                      background: '#e74c3c',
                      color: 'white',
                      border: 'none',
                      borderRadius: '50%',
                      minWidth: '32px',
                      minHeight: '32px',
                      width: '32px',
                      height: '32px',
                      fontSize: '1.2rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                      flexShrink: '0'
                    }}
                    title="Verwijder foto"
                  >
                    ×
                  </button>
                  <div style={{
                    position: 'absolute',
                    bottom: '0.5rem',
                    left: '0.5rem',
                    background: 'rgba(0,0,0,0.7)',
                    color: 'white',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    fontWeight: '600'
                  }}>
                    {category}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      </>
      )}
    </div>
  );
};

const RamenStep = ({ session, updateField, token, toast, stepConfigs, selectedRaamType, setSelectedRaamType, ramenSubstep: substep, setRamenSubstep: setSubstep }) => {
  const [uploading, setUploading] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  // Per-kamer mode (snelle/uitgebreide), dynamisch op selectedRaamType.
  const [mode, setMode] = useInspectionMode(token, `ramen-${selectedRaamType}`);

  // Reset substep naar triage wanneer de bewoner van kamer wisselt
  useEffect(() => {
    setSubstep('triage');
  }, [selectedRaamType, setSubstep]);

  // Initialize default values for all rooms (5 stars)
  useEffect(() => {
    const raamTypes = ['slaapkamer', 'woonkamerEnkel', 'woonkamerDubbel'];
    const fields = ['tochtafdichting', 'scharnieren', 'sluitwerk'];

    raamTypes.forEach(roomType => {
      fields.forEach(field => {
        const fieldPath = `kantelkiepramen.${roomType}.${field}`;
        const currentValue = session.kantelkiepramen?.[roomType]?.[field];

        // Only set default if field doesn't have a value yet
        if (currentValue === undefined || currentValue === null) {
          updateField(fieldPath, 5);
        }
      });

      // Initialize fotos array if it doesn't exist
      const currentFotos = session.kantelkiepramen?.[roomType]?.fotos;
      if (!currentFotos) {
        updateField(`kantelkiepramen.${roomType}.fotos`, []);
      }
    });
  }, []); // Run only once on mount

  // Helper function to check if a field is visible
  const isFieldVisible = (fieldPath) => {
    const stepConfig = stepConfigs?.find(config => config.stepId === 'ramen');
    if (!stepConfig || !stepConfig.fields || stepConfig.fields.length === 0) {
      return true; // Default to visible if no config
    }
    const field = stepConfig.fields.find(f => f.fieldPath === fieldPath);
    if (!field) {
      return true; // Default to visible if field not in config
    }
    return field.visible !== false;
  };

  const handlePhotoSelect = (e, category) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setPhotoPreview(reader.result);
      setSelectedFile(file);
      setSelectedCategory(category);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const confirmPhoto = async () => {
    if (!selectedFile || !selectedCategory) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('photo', selectedFile);
    formData.append('huisnummer', session.huisnummer);
    formData.append('location', `raam-${selectedRaamType}`);
    formData.append('detail', selectedCategory);

    try {
      const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:6001';
      const response = await fetch(`${backendUrl}/api/inspections/session/${token}/photo`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) throw new Error('Upload failed');

      const data = await response.json();

      const currentPhotos = session.kantelkiepramen?.[selectedRaamType]?.fotos || [];
      updateField(`kantelkiepramen.${selectedRaamType}.fotos`, [...currentPhotos, {
        filename: data.filename,
        url: data.url || `${backendUrl}/uploads/inspections/${data.filename}`,
        category: selectedCategory
      }]);

      setPhotoPreview(null);
      setSelectedFile(null);
      setSelectedCategory(null);
    } catch (err) {
      toast.error('Foto upload mislukt: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const cancelPhoto = () => {
    setPhotoPreview(null);
    setSelectedFile(null);
    setSelectedCategory(null);
  };

  const deletePhoto = (index) => {
    const currentPhotos = session.kantelkiepramen?.[selectedRaamType]?.fotos || [];
    const newPhotos = currentPhotos.filter((_, idx) => idx !== index);
    updateField(`kantelkiepramen.${selectedRaamType}.fotos`, newPhotos);
  };

  const raamTypes = [
    { id: 'slaapkamer', label: 'Slaapkamer' },
    { id: 'woonkamerEnkel', label: 'Eetkamer' },
    { id: 'woonkamerDubbel', label: 'Woonkamer' }
  ];

  const kamerLabel = raamTypes.find(t => t.id === selectedRaamType)?.label;
  const kamerIndex = RAAM_TYPES.indexOf(selectedRaamType) + 1;

  return (
    <div className="step-content">
      {/* Kamer-indicator — kamers lopen sequentieel via de wizard-nav */}
      <h2 style={{ marginTop: 0, marginBottom: '0.2rem', fontSize: '1.25rem' }}>
        Kantel-kiepraam in de {kamerLabel?.toLowerCase()}
      </h2>
      <p style={{ fontSize: '0.8rem', color: '#95a5a6', margin: '0 0 0.75rem 0' }}>
        Raam {kamerIndex} van {RAAM_TYPES.length}
      </p>

      {substep === 'foto' && (
      <>
      <OverzichtsfotoBlock
        session={session}
        updateField={updateField}
        token={token}
        toast={toast}
        location="kantelkiepramen"
        fotosPath={`kantelkiepramen.${selectedRaamType}.fotos`}
        label={`Overzichtsfoto van het raam in de ${raamTypes.find(t => t.id === selectedRaamType)?.label.toLowerCase()}`}
        hint={`Eén foto van het hele kantel-kiepraam in de ${raamTypes.find(t => t.id === selectedRaamType)?.label.toLowerCase()}, vanaf binnen.`}
        detailLabel={`overzicht-${selectedRaamType}`}
      />

      {isFieldVisible(`kantelkiepramen.${selectedRaamType}.opmerkingen`) && (
        <div className="form-group">
          <label>Korte opmerking <span style={{ fontWeight: 'normal', color: '#7f8c8d' }}>(optioneel)</span></label>
          <textarea
            value={session.kantelkiepramen?.[selectedRaamType]?.opmerkingen || ''}
            onChange={(e) => updateField(`kantelkiepramen.${selectedRaamType}.opmerkingen`, e.target.value)}
            className="form-input"
            rows="2"
            placeholder="Iets dat je toch even wilt noemen..."
          />
        </div>
      )}
      </>
      )}

      {substep === 'triage' && (
      <>
      <VoorvraagCard
        vraag="Hoe staat dit raam erbij?"
        hint="Klemt het raam, gaan de scharnieren stroef, of tocht het?"
        mode={mode}
        onSnel={() => { setMode('snelle'); setSubstep('foto'); }}
        onUitgebreid={() => { setMode('uitgebreide'); setSubstep('detail'); }}
      />

      <details style={{ marginTop: '1rem', fontSize: '0.85rem', color: '#7f8c8d' }}>
        <summary style={{ cursor: 'pointer' }}>Wat bekijk je?</summary>
        <ul style={{ margin: '0.4rem 0 0 0', paddingLeft: '1.2rem' }}>
          <li>Klemt het raam, of gaat het soepel open en dicht?</li>
          <li>Werken de scharnieren goed (geen piepen, niet doorzakken)?</li>
          <li>Sluit het raam goed af — geen tocht of kieren?</li>
        </ul>
        <p style={{ margin: '0.4rem 0 0 0' }}>
          Tip: probeer elk raam even open en dicht te doen — dat zegt vaak meer dan kijken.
        </p>
      </details>
      </>
      )}

      {substep === 'detail' && (
      <>
      <details style={{ marginBottom: '0.75rem', fontSize: '0.85rem', color: '#7f8c8d' }}>
        <summary style={{ cursor: 'pointer' }}>Hoe werkt de sterren-beoordeling?</summary>
        <p style={{ margin: '0.4rem 0 0 0' }}>
          <strong>5</strong> uitstekend, <strong>4</strong> goed, <strong>3</strong> redelijk, <strong>2</strong> matig, <strong>1</strong> slecht. Klik op de <strong>i</strong>-knop voor uitleg.
        </p>
      </details>

      {isFieldVisible(`kantelkiepramen.${selectedRaamType}.tochtafdichting`) && (
        <StarRating
          label="Tochtafdichting"
          value={session.kantelkiepramen?.[selectedRaamType]?.tochtafdichting || 5}
          onChange={(value) => updateField(`kantelkiepramen.${selectedRaamType}.tochtafdichting`, value)}
          info={fieldInfoFor('tochtafdichting')}
        />
      )}

      {isFieldVisible(`kantelkiepramen.${selectedRaamType}.scharnieren`) && (
        <StarRating
          label="Scharnieren"
          value={session.kantelkiepramen?.[selectedRaamType]?.scharnieren || 5}
          onChange={(value) => updateField(`kantelkiepramen.${selectedRaamType}.scharnieren`, value)}
          info={fieldInfoFor('scharnieren')}
        />
      )}

      {isFieldVisible(`kantelkiepramen.${selectedRaamType}.sluitwerk`) && (
        <StarRating
          label="Sluitwerk"
          value={session.kantelkiepramen?.[selectedRaamType]?.sluitwerk || 5}
          onChange={(value) => updateField(`kantelkiepramen.${selectedRaamType}.sluitwerk`, value)}
          info={fieldInfoFor('sluitwerk')}
        />
      )}
      </>
      )}

      {substep === 'foto' && mode === 'uitgebreide' && (
      <>
      {!photoPreview && (
        <div className="form-group" style={{ marginTop: '2rem' }}>
          <label>Detailfoto's van gebreken</label>
          <p style={{ fontSize: '0.9rem', color: '#7f8c8d', marginBottom: '0.5rem' }}>
            Maak foto's van specifieke gebreken
          </p>
          <input
            type="file"
            accept="image/*"
            capture="environment"
            onChange={(e) => handlePhotoSelect(e, 'detail')}
            style={{ display: 'none' }}
            id={`detail-input-${selectedRaamType}`}
            disabled={uploading}
          />
          <button
            type="button"
            onClick={() => document.getElementById(`detail-input-${selectedRaamType}`).click()}
            disabled={uploading}
            style={{
              width: '100%',
              padding: '1rem',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: uploading ? 'not-allowed' : 'pointer',
              boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)'
            }}
          >
            Maak foto
          </button>
        </div>
      )}

      {photoPreview && (
        <div style={{ marginTop: '1rem', padding: '1rem', background: '#f8f9fa', borderRadius: '8px' }}>
          <img
            src={photoPreview}
            alt="Preview"
            style={{
              width: '100%',
              maxHeight: '250px',
              objectFit: 'contain',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              marginBottom: '1rem'
            }}
          />
          <div style={{ display: 'flex', gap: '1rem' }}>
            <button
              onClick={confirmPhoto}
              disabled={uploading}
              style={{
                flex: 1,
                padding: '0.75rem',
                background: '#27ae60',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: uploading ? 'not-allowed' : 'pointer'
              }}
            >
              {uploading ? 'Uploaden...' : 'Bevestigen'}
            </button>
            <button
              onClick={cancelPhoto}
              disabled={uploading}
              style={{
                flex: 1,
                padding: '0.75rem',
                background: '#e74c3c',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: uploading ? 'not-allowed' : 'pointer'
              }}
            >
              Verwijderen
            </button>
          </div>
        </div>
      )}

      {session.kantelkiepramen?.[selectedRaamType]?.fotos && session.kantelkiepramen[selectedRaamType].fotos.length > 0 && (
        <div style={{ marginTop: '1.5rem' }}>
          <h3 style={{ marginBottom: '1rem', color: '#2c3e50' }}>
            Geüploade foto's ({session.kantelkiepramen[selectedRaamType].fotos.length}):
          </h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
            gap: '1rem'
          }}>
            {session.kantelkiepramen[selectedRaamType].fotos.map((foto, idx) => {
              const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:6001';
              const imageUrl = typeof foto === 'string'
                ? `${backendUrl}/uploads/inspections/${foto}`
                : foto.driveDisplayUrl || foto.url || `${backendUrl}/uploads/inspections/${foto.filename}`;

              let category = 'foto';
              if (typeof foto === 'object' && foto.category) {
                category = foto.category;
              } else if (typeof foto === 'string' && foto.includes('overzicht')) {
                category = 'overzicht';
              } else if (typeof foto === 'string' && foto.includes('detail')) {
                category = 'detail';
              }

              return (
                <div key={idx} style={{
                  position: 'relative',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}>
                  <img
                    src={imageUrl}
                    alt={`Raam ${category} ${idx + 1}`}
                    style={{
                      width: '100%',
                      height: '150px',
                      objectFit: 'contain',
                      backgroundColor: '#f8f9fa'
                    }}
                  />
                  <button
                    onClick={() => deletePhoto(idx)}
                    style={{
                      position: 'absolute',
                      top: '0.5rem',
                      right: '0.5rem',
                      background: '#e74c3c',
                      color: 'white',
                      border: 'none',
                      borderRadius: '50%',
                      minWidth: '32px',
                      minHeight: '32px',
                      width: '32px',
                      height: '32px',
                      fontSize: '1.2rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                      flexShrink: '0'
                    }}
                    title="Verwijder foto"
                  >
                    ×
                  </button>
                  <div style={{
                    position: 'absolute',
                    bottom: '0.5rem',
                    left: '0.5rem',
                    background: 'rgba(0,0,0,0.7)',
                    color: 'white',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    fontWeight: '600'
                  }}>
                    {category}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      </>
      )}
    </div>
  );
};

const BalkonKleinStep = ({ session, updateField, token, toast, stepConfigs, balkonKleinSubstep: substep, setBalkonKleinSubstep: setSubstep, balkonKleinDetailSection: detailSection, setBalkonKleinDetailSection: setDetailSection }) => {
  const [uploading, setUploading] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [mode, setMode] = useInspectionMode(token, 'balkonKlein');

  // Helper function to check if a field is visible
  const isFieldVisible = (fieldPath) => {
    const stepConfig = stepConfigs?.find(config => config.stepId === 'balkonKlein');
    if (!stepConfig || !stepConfig.fields || stepConfig.fields.length === 0) {
      return true; // Default to visible if no config
    }
    const field = stepConfig.fields.find(f => f.fieldPath === fieldPath);
    if (!field) {
      return true; // Default to visible if field not in config
    }
    return field.visible !== false;
  };

  const handlePhotoSelect = (e, category) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setPhotoPreview(reader.result);
      setSelectedFile(file);
      setSelectedCategory(category);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const confirmPhoto = async () => {
    if (!selectedFile || !selectedCategory) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('photo', selectedFile);
    formData.append('huisnummer', session.huisnummer);
    formData.append('location', 'balkonKlein');
    formData.append('detail', selectedCategory);

    try {
      const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:6001';
      const response = await fetch(`${backendUrl}/api/inspections/session/${token}/photo`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) throw new Error('Upload failed');

      const data = await response.json();

      const currentPhotos = session.balkonKlein?.fotos || [];
      updateField('balkonKlein.fotos', [...currentPhotos, {
        filename: data.filename,
        url: data.url || `${backendUrl}/uploads/inspections/${data.filename}`,
        category: selectedCategory
      }]);

      setPhotoPreview(null);
      setSelectedFile(null);
      setSelectedCategory(null);
    } catch (err) {
      toast.error('Foto upload mislukt: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const cancelPhoto = () => {
    setPhotoPreview(null);
    setSelectedFile(null);
    setSelectedCategory(null);
  };

  const deletePhoto = (index) => {
    const currentPhotos = session.balkonKlein?.fotos || [];
    const newPhotos = currentPhotos.filter((_, idx) => idx !== index);
    updateField('balkonKlein.fotos', newPhotos);
  };

  return (
    <div className="step-content">
      {substep === 'triage' && (
        <>
          <h2 style={{ marginTop: 0, marginBottom: '0.6rem', fontSize: '1.3rem' }}>Het kleine balkon</h2>

          <VoorvraagCard
            vraag="Hoe staat het kleine balkon erbij?"
            hint="Geen klein balkon, of alles in orde? Kies 'Alles in orde'."
            mode={mode}
            onSnel={() => { setMode('snelle'); setSubstep('foto'); }}
            onUitgebreid={() => { setMode('uitgebreide'); setSubstep('detail'); setDetailSection('hout'); }}
          />

          <details style={{ marginTop: '1rem', fontSize: '0.85rem', color: '#7f8c8d' }}>
            <summary style={{ cursor: 'pointer' }}>Wat bekijk je?</summary>
            <ul style={{ margin: '0.4rem 0 0 0', paddingLeft: '1.2rem' }}>
              <li>Hout en schilderwerk van kozijnen en balkondeur</li>
              <li>Het beton — oranje strepen (roest), losse stukjes, groen/algen</li>
              <li>De balustrade — rammelt 'ie?</li>
              <li>Het glas en de kit eromheen</li>
            </ul>
          </details>
        </>
      )}

      {substep === 'foto' && (
        <>
          <OverzichtsfotoBlock
            session={session}
            updateField={updateField}
            token={token}
            toast={toast}
            location="balkonKlein"
            fotosPath="balkonKlein.fotos"
            label="Overzichtsfoto van het kleine balkon"
            hint="Eén foto van het hele balkon, vanaf binnen of buiten."
          />

          {isFieldVisible('balkonKlein.opmerkingen') && (
            <div className="form-group">
              <label>Korte opmerking <span style={{ fontWeight: 'normal', color: '#7f8c8d' }}>(optioneel)</span></label>
              <textarea
                value={session.balkonKlein?.opmerkingen || ''}
                onChange={(e) => updateField('balkonKlein.opmerkingen', e.target.value)}
                className="form-input"
                rows="2"
                placeholder="Iets dat je toch even wilt noemen..."
              />
            </div>
          )}
        </>
      )}

      {substep === 'detail' && (
      <>
      <details style={{ marginBottom: '0.75rem', fontSize: '0.85rem', color: '#7f8c8d' }}>
        <summary style={{ cursor: 'pointer' }}>Hoe werkt de sterren-beoordeling?</summary>
        <p style={{ margin: '0.4rem 0 0 0' }}>
          <strong>5</strong> uitstekend, <strong>4</strong> goed, <strong>3</strong> redelijk, <strong>2</strong> matig, <strong>1</strong> slecht. Klik op de <strong>i</strong>-knop voor uitleg.
        </p>
      </details>

      {detailSection === 'hout' && (
      <>
      <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#2c3e50' }}>Houtwerk</h3>

      {isFieldVisible('balkonKlein.hout.houtaantasting') && (
        <StarRating
          label="Houtaantasting"
          value={session.balkonKlein?.hout?.houtaantasting || 5}
          onChange={(value) => updateField('balkonKlein.hout.houtaantasting', value)}
          info={fieldInfoFor('balkonKlein.hout.houtaantasting')}
        />
      )}

      {isFieldVisible('balkonKlein.hout.openVerbindingen') && (
        <StarRating
          label="Open verbindingen"
          value={session.balkonKlein?.hout?.openVerbindingen || 5}
          onChange={(value) => updateField('balkonKlein.hout.openVerbindingen', value)}
          info={fieldInfoFor('balkonKlein.hout.openVerbindingen')}
        />
      )}

      {isFieldVisible('balkonKlein.hout.scheuren') && (
        <StarRating
          label="Scheuren"
          value={session.balkonKlein?.hout?.scheuren || 5}
          onChange={(value) => updateField('balkonKlein.hout.scheuren', value)}
          info={fieldInfoFor('balkonKlein.hout.scheuren')}
        />
      )}

      {isFieldVisible('balkonKlein.hout.klemmenDraaiendeDelen') && (
        <StarRating
          label="Klemmen draaiende delen"
          value={session.balkonKlein?.hout?.klemmenDraaiendeDelen || 5}
          onChange={(value) => updateField('balkonKlein.hout.klemmenDraaiendeDelen', value)}
          info={fieldInfoFor('balkonKlein.hout.klemmenDraaiendeDelen')}
        />
      )}

      {isFieldVisible('balkonKlein.hout.scherpeKanten') && (
        <StarRating
          label="Scherpe kanten"
          value={session.balkonKlein?.hout?.scherpeKanten || 5}
          onChange={(value) => updateField('balkonKlein.hout.scherpeKanten', value)}
          info={fieldInfoFor('balkonKlein.hout.scherpeKanten')}
        />
      )}

      {isFieldVisible('balkonKlein.hout.houtvocht') && (
        <StarRating
          label="Houtvocht"
          value={session.balkonKlein?.hout?.houtvocht || 5}
          onChange={(value) => updateField('balkonKlein.hout.houtvocht', value)}
          info={fieldInfoFor('balkonKlein.hout.houtvocht')}
        />
      )}

      {isFieldVisible('balkonKlein.hout.tochtafdichting') && (
        <StarRating
          label="Tochtafdichting"
          value={session.balkonKlein?.hout?.tochtafdichting || 5}
          onChange={(value) => updateField('balkonKlein.hout.tochtafdichting', value)}
          info={fieldInfoFor('balkonKlein.hout.tochtafdichting')}
        />
      )}

      {isFieldVisible('balkonKlein.hout.scharnieren') && (
        <StarRating
          label="Scharnieren"
          value={session.balkonKlein?.hout?.scharnieren || 5}
          onChange={(value) => updateField('balkonKlein.hout.scharnieren', value)}
          info={fieldInfoFor('balkonKlein.hout.scharnieren')}
        />
      )}

      {isFieldVisible('balkonKlein.hout.sluitwerk') && (
        <StarRating
          label="Sluitwerk"
          value={session.balkonKlein?.hout?.sluitwerk || 5}
          onChange={(value) => updateField('balkonKlein.hout.sluitwerk', value)}
          info={fieldInfoFor('balkonKlein.hout.sluitwerk')}
        />
      )}
      </>
      )}

      {detailSection === 'schilder' && (
      <>
      <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#2c3e50' }}>Schilderwerk</h3>

      {isFieldVisible('balkonKlein.schilderwerk.glans') && (
        <StarRating
          label="Glans"
          value={session.balkonKlein?.schilderwerk?.glans || 5}
          onChange={(value) => updateField('balkonKlein.schilderwerk.glans', value)}
          info={fieldInfoFor('balkonKlein.schilderwerk.glans')}
        />
      )}

      {isFieldVisible('balkonKlein.schilderwerk.verkleuring') && (
        <StarRating
          label="Verkleuring"
          value={session.balkonKlein?.schilderwerk?.verkleuring || 5}
          onChange={(value) => updateField('balkonKlein.schilderwerk.verkleuring', value)}
          info={fieldInfoFor('balkonKlein.schilderwerk.verkleuring')}
        />
      )}

      {isFieldVisible('balkonKlein.schilderwerk.verfschade') && (
        <StarRating
          label="Verfschade"
          value={session.balkonKlein?.schilderwerk?.verfschade || 5}
          onChange={(value) => updateField('balkonKlein.schilderwerk.verfschade', value)}
          info={fieldInfoFor('balkonKlein.schilderwerk.verfschade')}
        />
      )}

      {isFieldVisible('balkonKlein.schilderwerk.hechting') && (
        <StarRating
          label="Hechting"
          value={session.balkonKlein?.schilderwerk?.hechting || 5}
          onChange={(value) => updateField('balkonKlein.schilderwerk.hechting', value)}
          info={fieldInfoFor('balkonKlein.schilderwerk.hechting')}
        />
      )}

      {isFieldVisible('balkonKlein.schilderwerk.regenschade') && (
        <StarRating
          label="Regenschade"
          value={session.balkonKlein?.schilderwerk?.regenschade || 5}
          onChange={(value) => updateField('balkonKlein.schilderwerk.regenschade', value)}
          info={fieldInfoFor('balkonKlein.schilderwerk.regenschade')}
        />
      )}

      {isFieldVisible('balkonKlein.schilderwerk.slechtBesnijwerk') && (
        <StarRating
          label="Slecht besnijwerk"
          value={session.balkonKlein?.schilderwerk?.slechtBesnijwerk || 5}
          onChange={(value) => updateField('balkonKlein.schilderwerk.slechtBesnijwerk', value)}
          info={fieldInfoFor('balkonKlein.schilderwerk.slechtBesnijwerk')}
        />
      )}

      {isFieldVisible('balkonKlein.schilderwerk.heiligeDagen') && (
        <StarRating
          label="Heilige dagen"
          value={session.balkonKlein?.schilderwerk?.heiligeDagen || 5}
          onChange={(value) => updateField('balkonKlein.schilderwerk.heiligeDagen', value)}
          info={fieldInfoFor('balkonKlein.schilderwerk.heiligeDagen')}
        />
      )}

      {isFieldVisible('balkonKlein.schilderwerk.insluitingen') && (
        <StarRating
          label="Insluitingen"
          value={session.balkonKlein?.schilderwerk?.insluitingen || 5}
          onChange={(value) => updateField('balkonKlein.schilderwerk.insluitingen', value)}
          info={fieldInfoFor('balkonKlein.schilderwerk.insluitingen')}
        />
      )}

      {isFieldVisible('balkonKlein.schilderwerk.zakkers') && (
        <StarRating
          label="Zakkers"
          value={session.balkonKlein?.schilderwerk?.zakkers || 5}
          onChange={(value) => updateField('balkonKlein.schilderwerk.zakkers', value)}
          info={fieldInfoFor('balkonKlein.schilderwerk.zakkers')}
        />
      )}
      </>
      )}

      {detailSection === 'glas' && (
      <>
      <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#2c3e50' }}>Beglazing</h3>

      {isFieldVisible('balkonKlein.glas.type') && (
        <div className="form-group">
          <label>Type glas</label>
          <input
            type="text"
            value={session.balkonKlein?.glas?.type || ''}
            onChange={(e) => updateField('balkonKlein.glas.type', e.target.value)}
            className="form-input"
            placeholder="Bijv. dubbel glas, HR++, etc."
          />
        </div>
      )}

      {isFieldVisible('balkonKlein.glas.gebrekenIsolatie') && (
        <StarRating
          label="Gebreken isolatie"
          value={session.balkonKlein?.glas?.gebrekenIsolatie || 5}
          onChange={(value) => updateField('balkonKlein.glas.gebrekenIsolatie', value)}
          info={fieldInfoFor('balkonKlein.glas.gebrekenIsolatie')}
        />
      )}

      {isFieldVisible('balkonKlein.glas.glasschade') && (
        <StarRating
          label="Glasschade"
          value={session.balkonKlein?.glas?.glasschade || 5}
          onChange={(value) => updateField('balkonKlein.glas.glasschade', value)}
          info={fieldInfoFor('balkonKlein.glas.glasschade')}
        />
      )}

      {isFieldVisible('balkonKlein.glas.gebrekenVentilatiekleppen') && (
        <StarRating
          label="Gebreken ventilatiekleppen"
          value={session.balkonKlein?.glas?.gebrekenVentilatiekleppen || 5}
          onChange={(value) => updateField('balkonKlein.glas.gebrekenVentilatiekleppen', value)}
          info={fieldInfoFor('balkonKlein.glas.gebrekenVentilatiekleppen')}
        />
      )}

      {isFieldVisible('balkonKlein.glas.gebrekenGlasafdichting') && (
        <StarRating
          label="Gebreken glasafdichting"
          value={session.balkonKlein?.glas?.gebrekenGlasafdichting || 5}
          onChange={(value) => updateField('balkonKlein.glas.gebrekenGlasafdichting', value)}
          info={fieldInfoFor('balkonKlein.glas.gebrekenGlasafdichting')}
        />
      )}

      {isFieldVisible('balkonKlein.glas.gebrekenGlasbevestiging') && (
        <StarRating
          label="Gebreken glasbevestiging"
          value={session.balkonKlein?.glas?.gebrekenGlasbevestiging || 5}
          onChange={(value) => updateField('balkonKlein.glas.gebrekenGlasbevestiging', value)}
          info={fieldInfoFor('balkonKlein.glas.gebrekenGlasbevestiging')}
        />
      )}
      </>
      )}

      {detailSection === 'beton' && (
      <>
      <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#2c3e50' }}>Beton</h3>

      {isFieldVisible('balkonKlein.beton.afgedrukteLoseDelen') && (
        <StarRating
          label="Afgedrukte/losse delen"
          value={session.balkonKlein?.beton?.afgedrukteLoseDelen || 5}
          onChange={(value) => updateField('balkonKlein.beton.afgedrukteLoseDelen', value)}
        />
      )}

      {isFieldVisible('balkonKlein.beton.corrosieWapening') && (
        <StarRating
          label="Corrosie wapening"
          value={session.balkonKlein?.beton?.corrosieWapening || 5}
          onChange={(value) => updateField('balkonKlein.beton.corrosieWapening', value)}
          info={fieldInfoFor('balkonKlein.beton.corrosieWapening')}
        />
      )}

      {isFieldVisible('balkonKlein.beton.chloriden') && (
        <StarRating
          label="Chloriden"
          value={session.balkonKlein?.beton?.chloriden || 5}
          onChange={(value) => updateField('balkonKlein.beton.chloriden', value)}
          info={fieldInfoFor('balkonKlein.beton.chloriden')}
        />
      )}

      {isFieldVisible('balkonKlein.beton.gietgallen') && (
        <StarRating
          label="Gietgallen"
          value={session.balkonKlein?.beton?.gietgallen || 5}
          onChange={(value) => updateField('balkonKlein.beton.gietgallen', value)}
          info={fieldInfoFor('balkonKlein.beton.gietgallen')}
        />
      )}

      {isFieldVisible('balkonKlein.beton.oranjeAanslag') && (
        <StarRating
          label="Oranje aanslag"
          value={session.balkonKlein?.beton?.oranjeAanslag || 5}
          onChange={(value) => updateField('balkonKlein.beton.oranjeAanslag', value)}
          info={fieldInfoFor('balkonKlein.beton.oranjeAanslag')}
        />
      )}

      {isFieldVisible('balkonKlein.beton.groeneAanslag') && (
        <StarRating
          label="Groene aanslag"
          value={session.balkonKlein?.beton?.groeneAanslag || 5}
          onChange={(value) => updateField('balkonKlein.beton.groeneAanslag', value)}
          info={fieldInfoFor('balkonKlein.beton.groeneAanslag')}
        />
      )}
      </>
      )}

      {detailSection === 'balustrade' && (
      <>
      <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#2c3e50' }}>Balustrade</h3>

      {isFieldVisible('balkonKlein.balustrade.stevigheid') && (
        <StarRating
          label="Stevigheid"
          value={session.balkonKlein?.balustrade?.stevigheid || 5}
          onChange={(value) => updateField('balkonKlein.balustrade.stevigheid', value)}
          info={fieldInfoFor('balkonKlein.balustrade.stevigheid')}
        />
      )}

      {isFieldVisible('balkonKlein.balustrade.duivenpinnen') && (
        <StarRating
          label="Duivenpinnen"
          value={session.balkonKlein?.balustrade?.duivenpinnen || 5}
          onChange={(value) => updateField('balkonKlein.balustrade.duivenpinnen', value)}
          info={fieldInfoFor('balkonKlein.balustrade.duivenpinnen')}
        />
      )}
      </>
      )}
      </>
      )}

      {substep === 'foto' && mode === 'uitgebreide' && (
      <>
      {!photoPreview && (
        <div className="form-group" style={{ marginTop: '2rem' }}>
          <label>Detailfoto's van gebreken</label>
          <p style={{ fontSize: '0.9rem', color: '#7f8c8d', marginBottom: '0.5rem' }}>
            Maak foto's van specifieke gebreken
          </p>
          <input
            type="file"
            accept="image/*"
            capture="environment"
            onChange={(e) => handlePhotoSelect(e, 'detail')}
            style={{ display: 'none' }}
            id="balkonklein-detail-input"
            disabled={uploading}
          />
          <button
            type="button"
            onClick={() => document.getElementById('balkonklein-detail-input').click()}
            disabled={uploading}
            style={{
              width: '100%',
              padding: '1rem',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: uploading ? 'not-allowed' : 'pointer',
              boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)'
            }}
          >
            Maak foto
          </button>
        </div>
      )}

      {photoPreview && (
        <div style={{ marginTop: '1rem', padding: '1rem', background: '#f8f9fa', borderRadius: '8px' }}>
          <img
            src={photoPreview}
            alt="Preview"
            style={{
              width: '100%',
              maxHeight: '250px',
              objectFit: 'contain',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              marginBottom: '1rem'
            }}
          />
          <div style={{ display: 'flex', gap: '1rem' }}>
            <button
              onClick={confirmPhoto}
              disabled={uploading}
              style={{
                flex: 1,
                padding: '0.75rem',
                background: '#27ae60',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: uploading ? 'not-allowed' : 'pointer'
              }}
            >
              {uploading ? 'Uploaden...' : 'Bevestigen'}
            </button>
            <button
              onClick={cancelPhoto}
              disabled={uploading}
              style={{
                flex: 1,
                padding: '0.75rem',
                background: '#e74c3c',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: uploading ? 'not-allowed' : 'pointer'
              }}
            >
              Verwijderen
            </button>
          </div>
        </div>
      )}

      {session.balkonKlein?.fotos && session.balkonKlein.fotos.length > 0 && (
        <div style={{ marginTop: '1.5rem' }}>
          <h3 style={{ marginBottom: '1rem', color: '#2c3e50' }}>Geüploade foto's ({session.balkonKlein.fotos.length}):</h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
            gap: '1rem'
          }}>
            {session.balkonKlein.fotos.map((foto, idx) => {
              const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:6001';
              const imageUrl = typeof foto === 'string'
                ? `${backendUrl}/uploads/inspections/${foto}`
                : foto.driveDisplayUrl || foto.url || `${backendUrl}/uploads/inspections/${foto.filename}`;

              let category = 'foto';
              if (typeof foto === 'object' && foto.category) category = foto.category;

              return (
                <div key={idx} style={{
                  position: 'relative',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}>
                  <img
                    src={imageUrl}
                    alt={`Balkon ${category} ${idx + 1}`}
                    style={{
                      width: '100%',
                      height: '150px',
                      objectFit: 'contain',
                      backgroundColor: '#f8f9fa'
                    }}
                  />
                  <button
                    onClick={() => deletePhoto(idx)}
                    style={{
                      position: 'absolute',
                      top: '0.5rem',
                      right: '0.5rem',
                      background: '#e74c3c',
                      color: 'white',
                      border: 'none',
                      borderRadius: '50%',
                      minWidth: '32px',
                      minHeight: '32px',
                      width: '32px',
                      height: '32px',
                      fontSize: '1.2rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                      flexShrink: '0'
                    }}
                    title="Verwijder foto"
                  >
                    ×
                  </button>
                  <div style={{
                    position: 'absolute',
                    bottom: '0.5rem',
                    left: '0.5rem',
                    background: 'rgba(0,0,0,0.7)',
                    color: 'white',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    fontWeight: '600'
                  }}>
                    {category}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      </>
      )}
    </div>
  );
};

const BalkonGrootStep = ({ session, updateField, token, toast, stepConfigs, balkonGrootSubstep: substep, setBalkonGrootSubstep: setSubstep, balkonGrootDetailSection: detailSection, setBalkonGrootDetailSection: setDetailSection }) => {
  const [uploading, setUploading] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [mode, setMode] = useInspectionMode(token, 'balkonGroot');
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Helper function to check if a field is visible
  const isFieldVisible = (fieldPath) => {
    const stepConfig = stepConfigs?.find(config => config.stepId === 'balkonGroot');
    if (!stepConfig || !stepConfig.fields || stepConfig.fields.length === 0) {
      return true; // Default to visible if no config
    }
    const field = stepConfig.fields.find(f => f.fieldPath === fieldPath);
    if (!field) {
      return true; // Default to visible if field not in config
    }
    return field.visible !== false;
  };

  const handlePhotoSelect = (e, category) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setPhotoPreview(reader.result);
      setSelectedFile(file);
      setSelectedCategory(category);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const confirmPhoto = async () => {
    if (!selectedFile || !selectedCategory) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('photo', selectedFile);
    formData.append('huisnummer', session.huisnummer);
    formData.append('location', 'balkonGroot');
    formData.append('detail', selectedCategory);

    try {
      const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:6001';
      const response = await fetch(`${backendUrl}/api/inspections/session/${token}/photo`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) throw new Error('Upload failed');

      const data = await response.json();

      const currentPhotos = session.balkonGroot?.fotos || [];
      updateField('balkonGroot.fotos', [...currentPhotos, {
        filename: data.filename,
        url: data.url || `${backendUrl}/uploads/inspections/${data.filename}`,
        category: selectedCategory
      }]);

      setPhotoPreview(null);
      setSelectedFile(null);
      setSelectedCategory(null);
    } catch (err) {
      toast.error('Foto upload mislukt: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const cancelPhoto = () => {
    setPhotoPreview(null);
    setSelectedFile(null);
    setSelectedCategory(null);
  };

  const deletePhoto = (index) => {
    const currentPhotos = session.balkonGroot?.fotos || [];
    const newPhotos = currentPhotos.filter((_, idx) => idx !== index);
    updateField('balkonGroot.fotos', newPhotos);
  };

  return (
    <div className="step-content">
      {substep === 'triage' && (
        <>
          <h2 style={{ marginTop: 0, marginBottom: '0.6rem', fontSize: '1.3rem' }}>Het grote balkon</h2>

          <VoorvraagCard
            vraag="Hoe staat het grote balkon erbij?"
            hint="Geen groot balkon, of alles in orde? Kies 'Alles in orde'."
            mode={mode}
            onSnel={() => { setMode('snelle'); setSubstep('foto'); }}
            onUitgebreid={() => { setMode('uitgebreide'); setSubstep('detail'); setDetailSection('hout'); }}
          />

          <details style={{ marginTop: '1rem', fontSize: '0.85rem', color: '#7f8c8d' }}>
            <summary style={{ cursor: 'pointer' }}>Wat bekijk je?</summary>
            <ul style={{ margin: '0.4rem 0 0 0', paddingLeft: '1.2rem' }}>
              <li>Hout en schilderwerk van kozijnen en balkondeur</li>
              <li>Het beton — oranje strepen (roest), losse stukjes, groen/algen</li>
              <li>De balustrade — rammelt 'ie?</li>
              <li>Het glas en de kit eromheen</li>
            </ul>
          </details>
        </>
      )}

      {substep === 'foto' && (
        <>
          <OverzichtsfotoBlock
            session={session}
            updateField={updateField}
            token={token}
            toast={toast}
            location="balkonGroot"
            fotosPath="balkonGroot.fotos"
            label="Overzichtsfoto van het grote balkon"
            hint="Eén foto van het hele balkon, vanaf binnen of buiten."
          />

          {isFieldVisible('balkonGroot.opmerkingen') && (
            <div className="form-group">
              <label>Korte opmerking <span style={{ fontWeight: 'normal', color: '#7f8c8d' }}>(optioneel)</span></label>
              <textarea
                value={session.balkonGroot?.opmerkingen || ''}
                onChange={(e) => updateField('balkonGroot.opmerkingen', e.target.value)}
                className="form-input"
                rows="2"
                placeholder="Iets dat je toch even wilt noemen..."
              />
            </div>
          )}
        </>
      )}

      {substep === 'detail' && (
      <>
      <details style={{ marginBottom: '0.75rem', fontSize: '0.85rem', color: '#7f8c8d' }}>
        <summary style={{ cursor: 'pointer' }}>Hoe werkt de sterren-beoordeling?</summary>
        <p style={{ margin: '0.4rem 0 0 0' }}>
          <strong>5</strong> uitstekend, <strong>4</strong> goed, <strong>3</strong> redelijk, <strong>2</strong> matig, <strong>1</strong> slecht. Klik op de <strong>i</strong>-knop voor uitleg.
        </p>
      </details>

      {detailSection === 'hout' && (
      <>
      <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#2c3e50' }}>Houtwerk</h3>

      {isFieldVisible('balkonGroot.hout.houtaantasting') && (
        <StarRating
          label="Houtaantasting"
          value={session.balkonGroot?.hout?.houtaantasting || 5}
          onChange={(value) => updateField('balkonGroot.hout.houtaantasting', value)}
          info={fieldInfoFor('balkonGroot.hout.houtaantasting')}
        />
      )}

      {isFieldVisible('balkonGroot.hout.openVerbindingen') && (
        <StarRating
          label="Open verbindingen"
          value={session.balkonGroot?.hout?.openVerbindingen || 5}
          onChange={(value) => updateField('balkonGroot.hout.openVerbindingen', value)}
          info={fieldInfoFor('balkonGroot.hout.openVerbindingen')}
        />
      )}

      {isFieldVisible('balkonGroot.hout.scheuren') && (
        <StarRating
          label="Scheuren"
          value={session.balkonGroot?.hout?.scheuren || 5}
          onChange={(value) => updateField('balkonGroot.hout.scheuren', value)}
          info={fieldInfoFor('balkonGroot.hout.scheuren')}
        />
      )}

      {isFieldVisible('balkonGroot.hout.klemmenDraaiendeDelen') && (
        <StarRating
          label="Klemmen draaiende delen"
          value={session.balkonGroot?.hout?.klemmenDraaiendeDelen || 5}
          onChange={(value) => updateField('balkonGroot.hout.klemmenDraaiendeDelen', value)}
          info={fieldInfoFor('balkonGroot.hout.klemmenDraaiendeDelen')}
        />
      )}

      {isFieldVisible('balkonGroot.hout.scherpeKanten') && (
        <StarRating
          label="Scherpe kanten"
          value={session.balkonGroot?.hout?.scherpeKanten || 5}
          onChange={(value) => updateField('balkonGroot.hout.scherpeKanten', value)}
          info={fieldInfoFor('balkonGroot.hout.scherpeKanten')}
        />
      )}

      {isFieldVisible('balkonGroot.hout.houtvocht') && (
        <StarRating
          label="Houtvocht"
          value={session.balkonGroot?.hout?.houtvocht || 5}
          onChange={(value) => updateField('balkonGroot.hout.houtvocht', value)}
          info={fieldInfoFor('balkonGroot.hout.houtvocht')}
        />
      )}

      {isFieldVisible('balkonGroot.hout.tochtafdichting') && (
        <StarRating
          label="Tochtafdichting"
          value={session.balkonGroot?.hout?.tochtafdichting || 5}
          onChange={(value) => updateField('balkonGroot.hout.tochtafdichting', value)}
          info={fieldInfoFor('balkonGroot.hout.tochtafdichting')}
        />
      )}

      {isFieldVisible('balkonGroot.hout.scharnieren') && (
        <StarRating
          label="Scharnieren"
          value={session.balkonGroot?.hout?.scharnieren || 5}
          onChange={(value) => updateField('balkonGroot.hout.scharnieren', value)}
          info={fieldInfoFor('balkonGroot.hout.scharnieren')}
        />
      )}

      {isFieldVisible('balkonGroot.hout.sluitwerk') && (
        <StarRating
          label="Sluitwerk"
          value={session.balkonGroot?.hout?.sluitwerk || 5}
          onChange={(value) => updateField('balkonGroot.hout.sluitwerk', value)}
          info={fieldInfoFor('balkonGroot.hout.sluitwerk')}
        />
      )}
      </>
      )}

      {detailSection === 'schilder' && (
      <>
      <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#2c3e50' }}>Schilderwerk</h3>

      {isFieldVisible('balkonGroot.schilderwerk.glans') && (
        <StarRating
          label="Glans"
          value={session.balkonGroot?.schilderwerk?.glans || 5}
          onChange={(value) => updateField('balkonGroot.schilderwerk.glans', value)}
          info={fieldInfoFor('balkonGroot.schilderwerk.glans')}
        />
      )}

      {isFieldVisible('balkonGroot.schilderwerk.verkleuring') && (
        <StarRating
          label="Verkleuring"
          value={session.balkonGroot?.schilderwerk?.verkleuring || 5}
          onChange={(value) => updateField('balkonGroot.schilderwerk.verkleuring', value)}
          info={fieldInfoFor('balkonGroot.schilderwerk.verkleuring')}
        />
      )}

      {isFieldVisible('balkonGroot.schilderwerk.verfschade') && (
        <StarRating
          label="Verfschade"
          value={session.balkonGroot?.schilderwerk?.verfschade || 5}
          onChange={(value) => updateField('balkonGroot.schilderwerk.verfschade', value)}
          info={fieldInfoFor('balkonGroot.schilderwerk.verfschade')}
        />
      )}

      {isFieldVisible('balkonGroot.schilderwerk.hechting') && (
        <StarRating
          label="Hechting"
          value={session.balkonGroot?.schilderwerk?.hechting || 5}
          onChange={(value) => updateField('balkonGroot.schilderwerk.hechting', value)}
          info={fieldInfoFor('balkonGroot.schilderwerk.hechting')}
        />
      )}

      {isFieldVisible('balkonGroot.schilderwerk.regenschade') && (
        <StarRating
          label="Regenschade"
          value={session.balkonGroot?.schilderwerk?.regenschade || 5}
          onChange={(value) => updateField('balkonGroot.schilderwerk.regenschade', value)}
          info={fieldInfoFor('balkonGroot.schilderwerk.regenschade')}
        />
      )}

      {isFieldVisible('balkonGroot.schilderwerk.slechtBesnijwerk') && (
        <StarRating
          label="Slecht besnijwerk"
          value={session.balkonGroot?.schilderwerk?.slechtBesnijwerk || 5}
          onChange={(value) => updateField('balkonGroot.schilderwerk.slechtBesnijwerk', value)}
          info={fieldInfoFor('balkonGroot.schilderwerk.slechtBesnijwerk')}
        />
      )}

      {isFieldVisible('balkonGroot.schilderwerk.heiligeDagen') && (
        <StarRating
          label="Heilige dagen"
          value={session.balkonGroot?.schilderwerk?.heiligeDagen || 5}
          onChange={(value) => updateField('balkonGroot.schilderwerk.heiligeDagen', value)}
          info={fieldInfoFor('balkonGroot.schilderwerk.heiligeDagen')}
        />
      )}

      {isFieldVisible('balkonGroot.schilderwerk.insluitingen') && (
        <StarRating
          label="Insluitingen"
          value={session.balkonGroot?.schilderwerk?.insluitingen || 5}
          onChange={(value) => updateField('balkonGroot.schilderwerk.insluitingen', value)}
          info={fieldInfoFor('balkonGroot.schilderwerk.insluitingen')}
        />
      )}

      {isFieldVisible('balkonGroot.schilderwerk.zakkers') && (
        <StarRating
          label="Zakkers"
          value={session.balkonGroot?.schilderwerk?.zakkers || 5}
          onChange={(value) => updateField('balkonGroot.schilderwerk.zakkers', value)}
          info={fieldInfoFor('balkonGroot.schilderwerk.zakkers')}
        />
      )}
      </>
      )}

      {detailSection === 'glas' && (
      <>
      <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#2c3e50' }}>Beglazing</h3>

      {isFieldVisible('balkonGroot.glas.type') && (
        <div className="form-group">
          <label>Type glas</label>
          <input
            type="text"
            value={session.balkonGroot?.glas?.type || ''}
            onChange={(e) => updateField('balkonGroot.glas.type', e.target.value)}
            className="form-input"
            placeholder="Bijv. dubbel glas, HR++, etc."
          />
        </div>
      )}

      {isFieldVisible('balkonGroot.glas.gebrekenIsolatie') && (
        <StarRating
          label="Gebreken isolatie"
          value={session.balkonGroot?.glas?.gebrekenIsolatie || 5}
          onChange={(value) => updateField('balkonGroot.glas.gebrekenIsolatie', value)}
          info={fieldInfoFor('balkonGroot.glas.gebrekenIsolatie')}
        />
      )}

      {isFieldVisible('balkonGroot.glas.glasschade') && (
        <StarRating
          label="Glasschade"
          value={session.balkonGroot?.glas?.glasschade || 5}
          onChange={(value) => updateField('balkonGroot.glas.glasschade', value)}
          info={fieldInfoFor('balkonGroot.glas.glasschade')}
        />
      )}

      {isFieldVisible('balkonGroot.glas.gebrekenVentilatiekleppen') && (
        <StarRating
          label="Gebreken ventilatiekleppen"
          value={session.balkonGroot?.glas?.gebrekenVentilatiekleppen || 5}
          onChange={(value) => updateField('balkonGroot.glas.gebrekenVentilatiekleppen', value)}
          info={fieldInfoFor('balkonGroot.glas.gebrekenVentilatiekleppen')}
        />
      )}

      {isFieldVisible('balkonGroot.glas.gebrekenGlasafdichting') && (
        <StarRating
          label="Gebreken glasafdichting"
          value={session.balkonGroot?.glas?.gebrekenGlasafdichting || 5}
          onChange={(value) => updateField('balkonGroot.glas.gebrekenGlasafdichting', value)}
          info={fieldInfoFor('balkonGroot.glas.gebrekenGlasafdichting')}
        />
      )}

      {isFieldVisible('balkonGroot.glas.gebrekenGlasbevestiging') && (
        <StarRating
          label="Gebreken glasbevestiging"
          value={session.balkonGroot?.glas?.gebrekenGlasbevestiging || 5}
          onChange={(value) => updateField('balkonGroot.glas.gebrekenGlasbevestiging', value)}
          info={fieldInfoFor('balkonGroot.glas.gebrekenGlasbevestiging')}
        />
      )}
      </>
      )}

      {detailSection === 'beton' && (
      <>
      <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#2c3e50' }}>Beton</h3>

      {isFieldVisible('balkonGroot.beton.afgedrukteLoseDelen') && (
        <StarRating
          label="Afgedrukte/losse delen"
          value={session.balkonGroot?.beton?.afgedrukteLoseDelen || 5}
          onChange={(value) => updateField('balkonGroot.beton.afgedrukteLoseDelen', value)}
        />
      )}

      {isFieldVisible('balkonGroot.beton.corrosieWapening') && (
        <StarRating
          label="Corrosie wapening"
          value={session.balkonGroot?.beton?.corrosieWapening || 5}
          onChange={(value) => updateField('balkonGroot.beton.corrosieWapening', value)}
          info={fieldInfoFor('balkonGroot.beton.corrosieWapening')}
        />
      )}

      {isFieldVisible('balkonGroot.beton.chloriden') && (
        <StarRating
          label="Chloriden"
          value={session.balkonGroot?.beton?.chloriden || 5}
          onChange={(value) => updateField('balkonGroot.beton.chloriden', value)}
          info={fieldInfoFor('balkonGroot.beton.chloriden')}
        />
      )}

      {isFieldVisible('balkonGroot.beton.gietgallen') && (
        <StarRating
          label="Gietgallen"
          value={session.balkonGroot?.beton?.gietgallen || 5}
          onChange={(value) => updateField('balkonGroot.beton.gietgallen', value)}
          info={fieldInfoFor('balkonGroot.beton.gietgallen')}
        />
      )}

      {isFieldVisible('balkonGroot.beton.oranjeAanslag') && (
        <StarRating
          label="Oranje aanslag"
          value={session.balkonGroot?.beton?.oranjeAanslag || 5}
          onChange={(value) => updateField('balkonGroot.beton.oranjeAanslag', value)}
          info={fieldInfoFor('balkonGroot.beton.oranjeAanslag')}
        />
      )}

      {isFieldVisible('balkonGroot.beton.groeneAanslag') && (
        <StarRating
          label="Groene aanslag"
          value={session.balkonGroot?.beton?.groeneAanslag || 5}
          onChange={(value) => updateField('balkonGroot.beton.groeneAanslag', value)}
          info={fieldInfoFor('balkonGroot.beton.groeneAanslag')}
        />
      )}
      </>
      )}

      {detailSection === 'balustrade' && (
      <>
      <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#2c3e50' }}>Balustrade</h3>

      {isFieldVisible('balkonGroot.balustrade.stevigheid') && (
        <StarRating
          label="Stevigheid"
          value={session.balkonGroot?.balustrade?.stevigheid || 5}
          onChange={(value) => updateField('balkonGroot.balustrade.stevigheid', value)}
          info={fieldInfoFor('balkonGroot.balustrade.stevigheid')}
        />
      )}

      {isFieldVisible('balkonGroot.balustrade.duivenpinnen') && (
        <StarRating
          label="Duivenpinnen"
          value={session.balkonGroot?.balustrade?.duivenpinnen || 5}
          onChange={(value) => updateField('balkonGroot.balustrade.duivenpinnen', value)}
          info={fieldInfoFor('balkonGroot.balustrade.duivenpinnen')}
        />
      )}
      </>
      )}

      {detailSection === 'spullen' && (
      <>
      <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#2c3e50' }}>Spullen op balkon</h3>

      {isFieldVisible('balkonGroot.spullen.meubilair') && (
        <div className="form-group">
          <label>Meubilair</label>
          <textarea
            value={session.balkonGroot?.spullen?.meubilair || ''}
            onChange={(e) => updateField('balkonGroot.spullen.meubilair', e.target.value)}
            className="form-input"
            rows="2"
            placeholder="Beschrijf eventueel meubilair op het balkon..."
          />
        </div>
      )}

      {isFieldVisible('balkonGroot.spullen.planten') && (
        <div className="form-group">
          <label>Planten</label>
          <textarea
            value={session.balkonGroot?.spullen?.planten || ''}
            onChange={(e) => updateField('balkonGroot.spullen.planten', e.target.value)}
            className="form-input"
            rows="2"
            placeholder="Beschrijf eventuele planten..."
          />
        </div>
      )}

      {isFieldVisible('balkonGroot.spullen.stroom') && (
        <div className="form-group">
          <label>Stroomvoorziening</label>
          <textarea
            value={session.balkonGroot?.spullen?.stroom || ''}
            onChange={(e) => updateField('balkonGroot.spullen.stroom', e.target.value)}
            className="form-input"
            rows="2"
            placeholder="Is er een stroomvoorziening? Wat is aangesloten?"
          />
        </div>
      )}
      </>
      )}
      </>
      )}

      {substep === 'foto' && mode === 'uitgebreide' && (
      <>
      {!photoPreview && (
        <div className="form-group" style={{ marginTop: '2rem' }}>
          <label>Detailfoto's van gebreken</label>
          <p style={{ fontSize: '0.9rem', color: '#7f8c8d', marginBottom: '0.5rem' }}>
            Maak foto's van specifieke gebreken
          </p>
          <input
            type="file"
            accept="image/*"
            capture="environment"
            onChange={(e) => handlePhotoSelect(e, 'detail')}
            style={{ display: 'none' }}
            id="balkongroot-detail-input"
            disabled={uploading}
          />
          <button
            type="button"
            onClick={() => document.getElementById('balkongroot-detail-input').click()}
            disabled={uploading}
            style={{
              width: '100%',
              padding: '1rem',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: uploading ? 'not-allowed' : 'pointer',
              boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)'
            }}
          >
            Maak foto
          </button>
        </div>
      )}

      {photoPreview && (
        <div style={{ marginTop: '1rem', padding: '1rem', background: '#f8f9fa', borderRadius: '8px' }}>
          <img
            src={photoPreview}
            alt="Preview"
            style={{
              width: '100%',
              maxHeight: '250px',
              objectFit: 'contain',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              marginBottom: '1rem'
            }}
          />
          <div style={{ display: 'flex', gap: '1rem' }}>
            <button
              onClick={confirmPhoto}
              disabled={uploading}
              style={{
                flex: 1,
                padding: '0.75rem',
                background: '#27ae60',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: uploading ? 'not-allowed' : 'pointer'
              }}
            >
              {uploading ? 'Uploaden...' : 'Bevestigen'}
            </button>
            <button
              onClick={cancelPhoto}
              disabled={uploading}
              style={{
                flex: 1,
                padding: '0.75rem',
                background: '#e74c3c',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: uploading ? 'not-allowed' : 'pointer'
              }}
            >
              Verwijderen
            </button>
          </div>
        </div>
      )}

      {session.balkonGroot?.fotos && session.balkonGroot.fotos.length > 0 && (
        <div style={{ marginTop: '1.5rem' }}>
          <h3 style={{ marginBottom: '1rem', color: '#2c3e50' }}>Geüploade foto's ({session.balkonGroot.fotos.length}):</h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
            gap: '1rem'
          }}>
            {session.balkonGroot.fotos.map((foto, idx) => {
              const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:6001';
              const imageUrl = typeof foto === 'string'
                ? `${backendUrl}/uploads/inspections/${foto}`
                : foto.driveDisplayUrl || foto.url || `${backendUrl}/uploads/inspections/${foto.filename}`;

              let category = 'foto';
              if (typeof foto === 'object' && foto.category) category = foto.category;

              return (
                <div key={idx} style={{
                  position: 'relative',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}>
                  <img
                    src={imageUrl}
                    alt={`Balkon ${category} ${idx + 1}`}
                    style={{
                      width: '100%',
                      height: '150px',
                      objectFit: 'contain',
                      backgroundColor: '#f8f9fa'
                    }}
                  />
                  <button
                    onClick={() => deletePhoto(idx)}
                    style={{
                      position: 'absolute',
                      top: '0.5rem',
                      right: '0.5rem',
                      background: '#e74c3c',
                      color: 'white',
                      border: 'none',
                      borderRadius: '50%',
                      minWidth: '32px',
                      minHeight: '32px',
                      width: '32px',
                      height: '32px',
                      fontSize: '1.2rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                      flexShrink: '0'
                    }}
                    title="Verwijder foto"
                  >
                    ×
                  </button>
                  <div style={{
                    position: 'absolute',
                    bottom: '0.5rem',
                    left: '0.5rem',
                    background: 'rgba(0,0,0,0.7)',
                    color: 'white',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    fontWeight: '600'
                  }}>
                    {category}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      </>
      )}
    </div>
  );
};

const AlgemeenStep = ({ session, updateField, token, stepConfigs, toast }) => {
  const [uploading, setUploading] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  // Helper to check if a field is visible
  const isFieldVisible = (fieldId) => {
    const stepConfig = stepConfigs?.find(config => config.stepId === 'algemeen');
    if (!stepConfig || !stepConfig.fields) return true;

    const field = stepConfig.fields.find(f => f.fieldId === fieldId);
    return field ? field.visible !== false : true;
  };

  const handlePhotoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setPhotoPreview(reader.result);
      setSelectedFile(file);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const confirmPhoto = async () => {
    if (!selectedFile) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('photo', selectedFile);
    formData.append('huisnummer', session.huisnummer);
    formData.append('location', 'algemeen');
    formData.append('detail', 'document');

    try {
      const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:6001';
      const response = await fetch(`${backendUrl}/api/inspections/session/${token}/photo`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) throw new Error('Upload failed');

      const data = await response.json();

      const currentDocs = session.algemeen?.extraDocumenten || [];
      updateField('algemeen.extraDocumenten', [...currentDocs, {
        filename: data.filename,
        url: data.url || `${backendUrl}/uploads/inspections/${data.filename}`
      }]);

      setPhotoPreview(null);
      setSelectedFile(null);
    } catch (err) {
      toast.error('Document upload mislukt: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const cancelPhoto = () => {
    setPhotoPreview(null);
    setSelectedFile(null);
  };

  const deleteDocument = (index) => {
    const currentDocs = session.algemeen?.extraDocumenten || [];
    const newDocs = currentDocs.filter((_, idx) => idx !== index);
    updateField('algemeen.extraDocumenten', newDocs);
  };

  return (
    <div className="step-content">
      <h2>Algemene vragen</h2>
      <p className="step-description">Tot slot nog enkele algemene vragen over uw woning.</p>

      {isFieldVisible('algemeen_aanpassingenAlgemeneGedeelte') && (
        <div className="form-group">
          <label>Aanpassingen aan VVE-onderdelen</label>
          <p style={{ fontSize: '0.9rem', color: '#7f8c8d', marginBottom: '0.5rem' }}>
            Heeft u — of een vorige bewoner — aanpassingen gedaan aan onderdelen die in
            onderhoud zijn van de VVE? Denk aan vervangen kozijnen, voor-/achterdeur of
            balkondeuren, gewijzigde of weggehaalde borstweringen, of een verwijderde
            buitendeur. <strong>Interne wijzigingen die geen VVE-onderdeel raken (bijv. een
            nieuwe keuken of badkamer-indeling) hoeft u hier niet te vermelden.</strong>
          </p>
          <textarea
            value={session.algemeen?.aanpassingenAlgemeneGedeelte || ''}
            onChange={(e) => updateField('algemeen.aanpassingenAlgemeneGedeelte', e.target.value)}
            className="form-input"
            rows="3"
            placeholder="Bijv. kozijn woonkamer vervangen in 2018, borstwering balkon verlaagd..."
          />
        </div>
      )}

      {isFieldVisible('algemeen_aantalGroepenMeterkast') && (
        <div className="form-group">
          <label>Aantal groepen meterkast</label>
          <p style={{ fontSize: '0.9rem', color: '#7f8c8d', marginBottom: '0.5rem' }}>
            De oorspronkelijke meterkast in het pand heeft <strong>3 groepen</strong> en zit in
            de centrale meterkast op de etagevloer. Bij verbouwingen (bijvoorbeeld een nieuwe
            keuken) is de meterkast soms uitgebreid met extra groepen.
          </p>
          <p style={{ fontSize: '0.9rem', color: '#7f8c8d', marginBottom: '0.5rem' }}>
            <strong>Hoe tellen?</strong> Open de meterkast en kijk naar het rijtje schakelaars
            (de zwarte of witte "schakeltjes" die je omhoog of omlaag kunt zetten — dit zijn de
            zekeringen of "automaten"). Tel die. De aardlekschakelaar — meestal breder, met een
            testknopje — telt <em>niet</em> mee. Vul het totaal in. Is het er 3, dan is uw kast
            origineel; meer dan 3 betekent dat de kast ooit is uitgebreid.
          </p>
          <input
            type="number"
            value={session.algemeen?.aantalGroepenMeterkast || ''}
            onChange={(e) => updateField('algemeen.aantalGroepenMeterkast', parseInt(e.target.value) || 0)}
            className="form-input"
            placeholder="Bijv. 3 (origineel) of 6"
          />
        </div>
      )}

      {isFieldVisible('algemeen_aansluitingenTVInternet') && (
        <div className="form-group">
          <label>Aansluitingen TV & Internet</label>
          <p style={{ fontSize: '0.9rem', color: '#7f8c8d', marginBottom: '0.5rem' }}>
            Welke aansluitingen zijn er voor TV en internet?
          </p>
          <textarea
            value={session.algemeen?.aansluitingenTVInternet || ''}
            onChange={(e) => updateField('algemeen.aansluitingenTVInternet', e.target.value)}
            className="form-input"
            rows="2"
            placeholder="Bijv. coax, UTP, glasvezel..."
          />
        </div>
      )}

      {isFieldVisible('algemeen_algemeenStateInterieur') && (
        <div className="form-group">
          <label>Algemene staat interieur</label>
          <p style={{ fontSize: '0.9rem', color: '#7f8c8d', marginBottom: '0.5rem' }}>
            Hoe zou u de algemene staat van het interieur omschrijven?
          </p>
          <textarea
            value={session.algemeen?.algemeenStateInterieur || ''}
            onChange={(e) => updateField('algemeen.algemeenStateInterieur', e.target.value)}
            className="form-input"
            rows="3"
            placeholder="Beschrijf de algemene staat..."
          />
        </div>
      )}

      {isFieldVisible('algemeen_toekomstigeOnderhoudsplannen') && (
        <div className="form-group">
          <label>Toekomstige onderhoudsplannen</label>
          <p style={{ fontSize: '0.9rem', color: '#7f8c8d', marginBottom: '0.5rem' }}>
            Bent u van plan om binnen afzienbare tijd onderhoud of verbouwingen uit te voeren?
          </p>
          <textarea
            value={session.algemeen?.toekomstigeOnderhoudsplannen || ''}
            onChange={(e) => updateField('algemeen.toekomstigeOnderhoudsplannen', e.target.value)}
            className="form-input"
            rows="3"
            placeholder="Beschrijf eventuele plannen..."
          />
        </div>
      )}

      {isFieldVisible('algemeen_eventueleGebreken') && (
        <div className="form-group">
          <label>Eventuele gebreken</label>
          <p style={{ fontSize: '0.9rem', color: '#7f8c8d', marginBottom: '0.5rem' }}>
            Zijn er nog andere gebreken of aandachtspunten die niet eerder genoemd zijn?
          </p>
          <textarea
            value={session.algemeen?.eventueleGebreken || ''}
            onChange={(e) => updateField('algemeen.eventueleGebreken', e.target.value)}
            className="form-input"
            rows="4"
            placeholder="Beschrijf eventuele gebreken..."
          />
        </div>
      )}

      {isFieldVisible('algemeen_extraOpmerkingen') && (
        <div className="form-group">
          <label>Extra opmerkingen</label>
          <textarea
            value={session.algemeen?.extraOpmerkingen || ''}
            onChange={(e) => updateField('algemeen.extraOpmerkingen', e.target.value)}
            className="form-input"
            rows="4"
            placeholder="Ruimte voor overige opmerkingen..."
          />
        </div>
      )}

      {/* Extra documenten upload */}
      {isFieldVisible('algemeen_extraDocumenten') && (
        <>
          <h3 style={{ marginTop: '2rem', marginBottom: '1rem', color: '#2c3e50' }}>Extra documenten</h3>
          <p style={{ fontSize: '0.9rem', color: '#7f8c8d', marginBottom: '1rem' }}>
            U kunt hier eventuele extra documenten of foto's uploaden (bijv. eerder uitgevoerde werkzaamheden, garantiebewijzen, etc.)
          </p>

          {!photoPreview && (
        <div className="form-group">
          <input
            type="file"
            accept="image/*,application/pdf"
            onChange={handlePhotoSelect}
            style={{ display: 'none' }}
            id="algemeen-document-input"
            disabled={uploading}
          />
          <button
            type="button"
            onClick={() => document.getElementById('algemeen-document-input').click()}
            disabled={uploading}
            style={{
              width: '100%',
              padding: '1rem',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: uploading ? 'not-allowed' : 'pointer',
              boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)'
            }}
          >
            Document toevoegen
          </button>
        </div>
      )}

      {photoPreview && (
        <div style={{ marginTop: '1rem', padding: '1rem', background: '#f8f9fa', borderRadius: '8px' }}>
          <img
            src={photoPreview}
            alt="Preview"
            style={{
              width: '100%',
              maxHeight: '250px',
              objectFit: 'contain',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              marginBottom: '1rem'
            }}
          />
          <div style={{ display: 'flex', gap: '1rem' }}>
            <button
              onClick={confirmPhoto}
              disabled={uploading}
              style={{
                flex: 1,
                padding: '0.75rem',
                background: '#27ae60',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: uploading ? 'not-allowed' : 'pointer'
              }}
            >
              {uploading ? 'Uploaden...' : 'Bevestigen'}
            </button>
            <button
              onClick={cancelPhoto}
              disabled={uploading}
              style={{
                flex: 1,
                padding: '0.75rem',
                background: '#e74c3c',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: uploading ? 'not-allowed' : 'pointer'
              }}
            >
              Verwijderen
            </button>
          </div>
        </div>
      )}

      {session.algemeen?.extraDocumenten && session.algemeen.extraDocumenten.length > 0 && (
        <div style={{ marginTop: '1.5rem' }}>
          <h3 style={{ marginBottom: '1rem', color: '#2c3e50' }}>
            Geüploade documenten ({session.algemeen.extraDocumenten.length}):
          </h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
            gap: '1rem'
          }}>
            {session.algemeen.extraDocumenten.map((doc, idx) => {
              const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:6001';
              const docUrl = typeof doc === 'string'
                ? `${backendUrl}/uploads/inspections/${doc}`
                : doc.url || `${backendUrl}/uploads/inspections/${doc.filename}`;

              return (
                <div key={idx} style={{
                  position: 'relative',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}>
                  <img
                    src={docUrl}
                    alt={`Document ${idx + 1}`}
                    style={{
                      width: '100%',
                      height: '150px',
                      objectFit: 'contain',
                      backgroundColor: '#f8f9fa'
                    }}
                  />
                  <button
                    onClick={() => deleteDocument(idx)}
                    style={{
                      position: 'absolute',
                      top: '0.5rem',
                      right: '0.5rem',
                      background: '#e74c3c',
                      color: 'white',
                      border: 'none',
                      borderRadius: '50%',
                      minWidth: '32px',
                      minHeight: '32px',
                      width: '32px',
                      height: '32px',
                      fontSize: '1.2rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                      flexShrink: '0'
                    }}
                    title="Verwijder document"
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
};

// Gebruikersonderhoud Step 1: Apparaten
const GebruikersonderhoudApparatenStep = ({ session, updateField, stepConfigs, installatiesPage: page }) => {
  // Initialize default values for toggle fields
  useEffect(() => {
    if (session.gebruikersonderhoud?.mechanischeAfvoer === undefined) {
      updateField('gebruikersonderhoud.mechanischeAfvoer', 'oke');
    }
  }, []);

  // Helper function to check if a field is visible
  const isFieldVisible = (fieldPath) => {
    const stepConfig = stepConfigs?.find(config => config.stepId === 'gebruikersonderhoud1');
    if (!stepConfig || !stepConfig.fields || stepConfig.fields.length === 0) {
      return true; // Default to visible if no config
    }
    const field = stepConfig.fields.find(f => f.fieldPath === fieldPath);
    if (!field) {
      return true; // Default to visible if field not in config
    }
    return field.visible !== false;
  };

  return (
    <div className="step-content">
      {page === 'gas' && (
        <>
          <h2 style={{ marginTop: 0, marginBottom: '0.6rem', fontSize: '1.3rem' }}>Installaties — gas</h2>

          {isFieldVisible('gebruikersonderhoud.gasaansluiting') && (
            <div className="form-group">
              <label>Gasaansluiting</label>
              <Toggle
                value={session.gebruikersonderhoud?.gasaansluiting}
                onChange={(val) => updateField('gebruikersonderhoud.gasaansluiting', val)}
                options={['nee', 'ja']}
              />
            </div>
          )}

          {isFieldVisible('gebruikersonderhoud.gasaansluitingOpmerking') && (
            <div className="form-group">
              <label>Opmerking gasaansluiting</label>
              <textarea
                value={session.gebruikersonderhoud?.gasaansluitingOpmerking || ''}
                onChange={(e) => updateField('gebruikersonderhoud.gasaansluitingOpmerking', e.target.value)}
                className="form-input"
                rows="2"
                placeholder="Eventuele opmerkingen..."
              />
            </div>
          )}
        </>
      )}

      {page === 'apparaten' && (
        <>
          <h2 style={{ marginTop: 0, marginBottom: '0.6rem', fontSize: '1.3rem' }}>Installaties — apparatuur</h2>

          {isFieldVisible('gebruikersonderhoud.kookplaat') && (
            <div className="form-group">
              <label>Kookplaat</label>
              <Toggle
                value={session.gebruikersonderhoud?.kookplaat}
                onChange={(val) => updateField('gebruikersonderhoud.kookplaat', val)}
                options={['nee', 'ja']}
              />
            </div>
          )}

          {isFieldVisible('gebruikersonderhoud.oven') && (
            <div className="form-group">
              <label>Oven(s)</label>
              <Toggle
                value={session.gebruikersonderhoud?.oven}
                onChange={(val) => updateField('gebruikersonderhoud.oven', val)}
                options={['nee', 'ja']}
              />
            </div>
          )}

          {isFieldVisible('gebruikersonderhoud.koelkast') && (
            <div className="form-group">
              <label>Koelkast</label>
              <Toggle
                value={session.gebruikersonderhoud?.koelkast}
                onChange={(val) => updateField('gebruikersonderhoud.koelkast', val)}
                options={['nee', 'ja']}
              />
            </div>
          )}

          {isFieldVisible('gebruikersonderhoud.diepvries') && (
            <div className="form-group">
              <label>Diepvries</label>
              <Toggle
                value={session.gebruikersonderhoud?.diepvries}
                onChange={(val) => updateField('gebruikersonderhoud.diepvries', val)}
                options={['nee', 'ja']}
              />
            </div>
          )}

          {isFieldVisible('gebruikersonderhoud.cooker') && (
            <div className="form-group">
              <label>Cooker (afzuigkap)</label>
              <Toggle
                value={session.gebruikersonderhoud?.cooker}
                onChange={(val) => updateField('gebruikersonderhoud.cooker', val)}
                options={['nee', 'ja']}
              />
            </div>
          )}
        </>
      )}

      {page === 'ventilatie' && (
        <>
          <h2 style={{ marginTop: 0, marginBottom: '0.6rem', fontSize: '1.3rem' }}>Installaties — ventilatie</h2>

          {isFieldVisible('gebruikersonderhoud.mechanischeAfvoer') && (
            <div className="form-group">
              <label>Mechanische afvoer</label>
              <p style={{ fontSize: '0.85rem', color: '#7f8c8d', margin: '0.25rem 0 0.5rem 0' }}>
                Centrale afzuiging — werkt de motor en zuigt elk ventiel? Twijfel: houd een vel papier tegen het rooster, moet blijven plakken.
              </p>
              <Toggle
                value={session.gebruikersonderhoud?.mechanischeAfvoer}
                onChange={(val) => updateField('gebruikersonderhoud.mechanischeAfvoer', val)}
                options={['niet oke', 'weet niet', 'oke']}
              />
            </div>
          )}

          {isFieldVisible('gebruikersonderhoud.apparatuurOpmerkingen') && (
            <div className="form-group">
              <label>Opmerkingen apparatuur</label>
              <textarea
                value={session.gebruikersonderhoud?.apparatuurOpmerkingen || ''}
                onChange={(e) => updateField('gebruikersonderhoud.apparatuurOpmerkingen', e.target.value)}
                className="form-input"
                rows="2"
                placeholder="Iets dat we moeten weten over de apparatuur..."
              />
            </div>
          )}
        </>
      )}

      {page === 'meterkast' && (
        <>
          <h2 style={{ marginTop: 0, marginBottom: '0.6rem', fontSize: '1.3rem' }}>Installaties — meterkast</h2>

          <div className="form-group">
            <label>Aantal groepen meterkast</label>
            <details style={{ fontSize: '0.85rem', color: '#7f8c8d', margin: '0.25rem 0 0.5rem 0' }}>
              <summary style={{ cursor: 'pointer' }}>Hoe tel ik de groepen?</summary>
              <p style={{ margin: '0.4rem 0 0 0' }}>
                De oorspronkelijke meterkast heeft <strong>3 groepen</strong>. Open de kast en tel het rijtje
                schakelaars (de zekeringen/"automaten"). De aardlekschakelaar — breder, met testknopje — telt
                <em>niet</em> mee. Meer dan 3 betekent dat de kast ooit is uitgebreid.
              </p>
            </details>
            <input
              type="number"
              value={session.algemeen?.aantalGroepenMeterkast || ''}
              onChange={(e) => updateField('algemeen.aantalGroepenMeterkast', parseInt(e.target.value) || 0)}
              className="form-input"
              placeholder="Bijv. 3 (origineel) of 6"
            />
          </div>

          <div className="form-group">
            <label>Extra groepen meterkast <span style={{ fontWeight: 'normal', color: '#7f8c8d' }}>(optioneel)</span></label>
            <input
              type="text"
              value={session.gebruikersonderhoud?.extraGroepen || ''}
              onChange={(e) => updateField('gebruikersonderhoud.extraGroepen', e.target.value)}
              className="form-input"
              placeholder="Bijv. losse groep voor wasmachine"
            />
          </div>

          <div className="form-group">
            <label>Opmerking meterkast <span style={{ fontWeight: 'normal', color: '#7f8c8d' }}>(optioneel)</span></label>
            <textarea
              value={session.gebruikersonderhoud?.groepenOpmerking || ''}
              onChange={(e) => updateField('gebruikersonderhoud.groepenOpmerking', e.target.value)}
              className="form-input"
              rows="2"
            />
          </div>
        </>
      )}
    </div>
  );
};

// Gebruikersonderhoud Step 2: Aansluitingen
const GebruikersonderhoudAansluitingenStep = ({ session, updateField, stepConfigs }) => {
  // Helper function to check if a field is visible
  const isFieldVisible = (fieldPath) => {
    const stepConfig = stepConfigs?.find(config => config.stepId === 'gebruikersonderhoud2');
    if (!stepConfig || !stepConfig.fields || stepConfig.fields.length === 0) {
      return true; // Default to visible if no config
    }
    const field = stepConfig.fields.find(f => f.fieldPath === fieldPath);
    if (!field) {
      return true; // Default to visible if field not in config
    }
    return field.visible !== false;
  };

  return (
    <div className="step-content">
      <h2>Gebruikersonderhoud: Aansluitingen</h2>
      <p className="step-description">Vul aan welke aansluitingen aanwezig zijn.</p>

      <h3 style={{ marginTop: '1.5rem', marginBottom: '1rem', color: '#2c3e50' }}>TV & Internet</h3>

      {isFieldVisible('gebruikersonderhoud.aansluitingTV') && (
        <div className="form-group">
          <label>Aansluiting TV</label>
          <Toggle
            value={session.gebruikersonderhoud?.aansluitingTV}
            onChange={(val) => updateField('gebruikersonderhoud.aansluitingTV', val)}
            options={['nee', 'ja']}
          />
        </div>
      )}

      {isFieldVisible('gebruikersonderhoud.tvOpmerking') && (
        <div className="form-group">
          <label>Opmerking TV</label>
          <textarea
            value={session.gebruikersonderhoud?.tvOpmerking || ''}
            onChange={(e) => updateField('gebruikersonderhoud.tvOpmerking', e.target.value)}
            className="form-input"
            rows="2"
          />
        </div>
      )}

      {isFieldVisible('gebruikersonderhoud.sdl') && (
        <div className="form-group">
          <label>ADSL</label>
          <Toggle
            value={session.gebruikersonderhoud?.sdl}
            onChange={(val) => updateField('gebruikersonderhoud.sdl', val)}
            options={['nee', 'ja']}
          />
        </div>
      )}

      {isFieldVisible('gebruikersonderhoud.glasvezel') && (
        <div className="form-group">
          <label>Glasvezel</label>
          <Toggle
            value={session.gebruikersonderhoud?.glasvezel}
            onChange={(val) => updateField('gebruikersonderhoud.glasvezel', val)}
            options={['nee', 'ja']}
          />
        </div>
      )}

      {isFieldVisible('gebruikersonderhoud.internetOpmerking') && (
        <div className="form-group">
          <label>Opmerking internet</label>
          <textarea
            value={session.gebruikersonderhoud?.internetOpmerking || ''}
            onChange={(e) => updateField('gebruikersonderhoud.internetOpmerking', e.target.value)}
            className="form-input"
            rows="2"
          />
        </div>
      )}

      {isFieldVisible('gebruikersonderhoud.analogeTelefoonlijn') && (
        <div className="form-group">
          <label>Analoge telefoonlijn</label>
          <Toggle
            value={session.gebruikersonderhoud?.analogeTelefoonlijn}
            onChange={(val) => updateField('gebruikersonderhoud.analogeTelefoonlijn', val)}
            options={['nee', 'ja']}
          />
        </div>
      )}

      {isFieldVisible('gebruikersonderhoud.telefoonOpmerking') && (
        <div className="form-group">
          <label>Opmerking telefoon</label>
          <textarea
            value={session.gebruikersonderhoud?.telefoonOpmerking || ''}
            onChange={(e) => updateField('gebruikersonderhoud.telefoonOpmerking', e.target.value)}
            className="form-input"
            rows="2"
          />
        </div>
      )}

      <h3 style={{ marginTop: '2rem', marginBottom: '1rem', color: '#2c3e50' }}>Intercom</h3>

      {isFieldVisible('gebruikersonderhoud.intercomGeluid') && (
        <div className="form-group">
          <label>Intercom Geluid</label>
          <p style={{ fontSize: '0.9rem', color: '#7f8c8d', marginBottom: '0.5rem' }}>
            Iedere intercom heeft een spreek-/luisterverbinding. Werkt deze: hoor je iemand bij de
            voordeur en hoort die u terug?
          </p>
          <Toggle
            value={session.gebruikersonderhoud?.intercomGeluid}
            onChange={(val) => updateField('gebruikersonderhoud.intercomGeluid', val)}
            options={['nee', 'ja']}
          />
        </div>
      )}

      {isFieldVisible('gebruikersonderhoud.intercomBeeld') && (
        <div className="form-group">
          <label>Intercom Beeld</label>
          <p style={{ fontSize: '0.9rem', color: '#7f8c8d', marginBottom: '0.5rem' }}>
            Sommige intercoms hebben ook een schermpje met camerabeeld. Heeft uw intercom dat, en zo ja,
            werkt het beeld?
          </p>
          <Toggle
            value={session.gebruikersonderhoud?.intercomBeeld}
            onChange={(val) => updateField('gebruikersonderhoud.intercomBeeld', val)}
            options={['nee', 'ja']}
          />
        </div>
      )}

      {isFieldVisible('gebruikersonderhoud.intercomOpmerking') && (
        <div className="form-group">
          <label>Opmerking intercom</label>
          <textarea
            value={session.gebruikersonderhoud?.intercomOpmerking || ''}
            onChange={(e) => updateField('gebruikersonderhoud.intercomOpmerking', e.target.value)}
            className="form-input"
            rows="2"
          />
        </div>
      )}
    </div>
  );
};

// Gebruikersonderhoud Step 3: Ventilatie & Verwarming
const GebruikersonderhoudVentilatieVerwarmingStep = ({ session, updateField, stepConfigs, ventilatiePage: page }) => {
  // Initialize default values for toggle fields
  useEffect(() => {
    const okeDefaults = [
      'luchtafvoerKeuken', 'luchtafvoerBadkamer', 'luchtafvoerWC',
      'afvoerBadkamer', 'afvoerKeuken', 'afvoerWC'
    ];
    okeDefaults.forEach(field => {
      if (session.gebruikersonderhoud?.[field] === undefined) {
        updateField(`gebruikersonderhoud.${field}`, 'oke');
      }
    });
  }, []);

  // Helper function to check if a field is visible
  const isFieldVisible = (fieldPath) => {
    const stepConfig = stepConfigs?.find(config => config.stepId === 'gebruikersonderhoud3');
    if (!stepConfig || !stepConfig.fields || stepConfig.fields.length === 0) {
      return true; // Default to visible if no config
    }
    const field = stepConfig.fields.find(f => f.fieldPath === fieldPath);
    if (!field) {
      return true; // Default to visible if field not in config
    }
    return field.visible !== false;
  };

  // Cross-step visibility: water-afvoer fields are configured under stap 4
  // (gebruikersonderhoud4) maar worden hier gerendered.
  const isStep4FieldVisible = (fieldPath) => {
    const stepConfig = stepConfigs?.find(c => c.stepId === 'gebruikersonderhoud4');
    if (!stepConfig || !stepConfig.fields || stepConfig.fields.length === 0) return true;
    const field = stepConfig.fields.find(f => f.fieldPath === fieldPath);
    if (!field) return true;
    return field.visible !== false;
  };

  return (
    <div className="step-content">
      {page === 'lucht' && (
      <>
      <h2 style={{ marginTop: 0, marginBottom: '0.6rem', fontSize: '1.3rem' }}>Luchtafvoer</h2>

      {isFieldVisible('gebruikersonderhoud.luchtafvoerKeuken') && (
        <div className="form-group">
          <label>Luchtafvoer keuken</label>
          <Toggle
            value={session.gebruikersonderhoud?.luchtafvoerKeuken}
            onChange={(val) => updateField('gebruikersonderhoud.luchtafvoerKeuken', val)}
            options={['niet oke', 'oke']}
          />
        </div>
      )}

      {isFieldVisible('gebruikersonderhoud.luchtafvoerBadkamer') && (
        <div className="form-group">
          <label>Luchtafvoer badkamer</label>
          <Toggle
            value={session.gebruikersonderhoud?.luchtafvoerBadkamer}
            onChange={(val) => updateField('gebruikersonderhoud.luchtafvoerBadkamer', val)}
            options={['niet oke', 'oke']}
          />
        </div>
      )}

      {isFieldVisible('gebruikersonderhoud.luchtafvoerWC') && (
        <div className="form-group">
          <label>Luchtafvoer WC</label>
          <Toggle
            value={session.gebruikersonderhoud?.luchtafvoerWC}
            onChange={(val) => updateField('gebruikersonderhoud.luchtafvoerWC', val)}
            options={['niet oke', 'oke']}
          />
        </div>
      )}

      {isFieldVisible('gebruikersonderhoud.afvoerOpmerkingen') && (
        <div className="form-group">
          <label>Opmerkingen afvoer</label>
          <textarea
            value={session.gebruikersonderhoud?.afvoerOpmerkingen || ''}
            onChange={(e) => updateField('gebruikersonderhoud.afvoerOpmerkingen', e.target.value)}
            className="form-input"
            rows="2"
            placeholder="Bijv. luchtjes, problemen, etc."
          />
        </div>
      )}
      </>
      )}

      {page === 'verwarming' && (
      <>
      <h2 style={{ marginTop: 0, marginBottom: '0.6rem', fontSize: '1.3rem' }}>Verwarming per ruimte</h2>
      <p style={{ fontSize: '0.9rem', color: '#7f8c8d', marginBottom: '1rem' }}>
        Geef per ruimte aan of er <em>nog</em> een verwarmingselement aanwezig is (radiator,
        convector, vloerverwarming). Originele radiatoren zijn soms verwijderd bij verbouwingen.
        De werking — wordt elke radiator warm, lekkage, ruis — beschrijft u onderaan in
        "Opmerking verwarming".
      </p>

      {isFieldVisible('gebruikersonderhoud.verwarmingWoonkamer1') && (
        <div className="form-group">
          <label>Verwarming Woonkamer 1</label>
          <Toggle
            value={session.gebruikersonderhoud?.verwarmingWoonkamer1}
            onChange={(val) => updateField('gebruikersonderhoud.verwarmingWoonkamer1', val)}
            options={['nee', 'ja']}
          />
        </div>
      )}

      {isFieldVisible('gebruikersonderhoud.verwarmingWoonkamer2') && (
        <div className="form-group">
          <label>Verwarming Woonkamer 2</label>
          <Toggle
            value={session.gebruikersonderhoud?.verwarmingWoonkamer2}
            onChange={(val) => updateField('gebruikersonderhoud.verwarmingWoonkamer2', val)}
            options={['nee', 'ja']}
          />
        </div>
      )}

      {isFieldVisible('gebruikersonderhoud.verwarmingKeuken') && (
        <div className="form-group">
          <label>Verwarming Keuken</label>
          <Toggle
            value={session.gebruikersonderhoud?.verwarmingKeuken}
            onChange={(val) => updateField('gebruikersonderhoud.verwarmingKeuken', val)}
            options={['nee', 'ja']}
          />
        </div>
      )}

      {isFieldVisible('gebruikersonderhoud.verwarmingBorrelkamer') && (
        <div className="form-group">
          <label>Verwarming Borrelkamer</label>
          <Toggle
            value={session.gebruikersonderhoud?.verwarmingBorrelkamer}
            onChange={(val) => updateField('gebruikersonderhoud.verwarmingBorrelkamer', val)}
            options={['nee', 'ja']}
          />
        </div>
      )}

      {isFieldVisible('gebruikersonderhoud.verwarmingWerkkamer') && (
        <div className="form-group">
          <label>Verwarming Werkkamer</label>
          <Toggle
            value={session.gebruikersonderhoud?.verwarmingWerkkamer}
            onChange={(val) => updateField('gebruikersonderhoud.verwarmingWerkkamer', val)}
            options={['nee', 'ja']}
          />
        </div>
      )}

      {isFieldVisible('gebruikersonderhoud.verwarmingSlaapkamer') && (
        <div className="form-group">
          <label>Verwarming Slaapkamer</label>
          <Toggle
            value={session.gebruikersonderhoud?.verwarmingSlaapkamer}
            onChange={(val) => updateField('gebruikersonderhoud.verwarmingSlaapkamer', val)}
            options={['nee', 'ja']}
          />
        </div>
      )}

      {isFieldVisible('gebruikersonderhoud.verwarmingBadkamer') && (
        <div className="form-group">
          <label>Verwarming Badkamer</label>
          <Toggle
            value={session.gebruikersonderhoud?.verwarmingBadkamer}
            onChange={(val) => updateField('gebruikersonderhoud.verwarmingBadkamer', val)}
            options={['nee', 'ja']}
          />
        </div>
      )}

      {isFieldVisible('gebruikersonderhoud.verwarmingOpmerking') && (
        <div className="form-group">
          <label>Opmerking verwarming</label>
          <p style={{ fontSize: '0.85rem', color: '#7f8c8d', margin: '0.25rem 0 0.5rem 0' }}>
            Bijv. niet alle radiatoren worden warm, tikkende geluiden, lekkage.
          </p>
          <textarea
            value={session.gebruikersonderhoud?.verwarmingOpmerking || ''}
            onChange={(e) => updateField('gebruikersonderhoud.verwarmingOpmerking', e.target.value)}
            className="form-input"
            rows="2"
            placeholder="Bijv. radiator slaapkamer wordt niet warm..."
          />
        </div>
      )}
      </>
      )}

      {page === 'afvoer' && (
      <>
      <h2 style={{ marginTop: 0, marginBottom: '0.6rem', fontSize: '1.3rem' }}>Afvoer (water)</h2>
      <p style={{ fontSize: '0.9rem', color: '#7f8c8d', marginBottom: '1rem' }}>
        Geef aan of de waterafvoer per ruimte goed werkt. Twee veelvoorkomende klachten:
        het water loopt langzaam weg, of er komen luchtjes uit de afvoer. Beide horen onder
        "niet oké".
      </p>

      {isStep4FieldVisible('gebruikersonderhoud.afvoerBadkamer') && (
        <div className="form-group">
          <label>Afvoer badkamer</label>
          <Toggle
            value={session.gebruikersonderhoud?.afvoerBadkamer}
            onChange={(val) => updateField('gebruikersonderhoud.afvoerBadkamer', val)}
            options={['niet oke', 'oke']}
          />
        </div>
      )}

      {isStep4FieldVisible('gebruikersonderhoud.afvoerKeuken') && (
        <div className="form-group">
          <label>Afvoer keuken</label>
          <Toggle
            value={session.gebruikersonderhoud?.afvoerKeuken}
            onChange={(val) => updateField('gebruikersonderhoud.afvoerKeuken', val)}
            options={['niet oke', 'oke']}
          />
        </div>
      )}

      {isStep4FieldVisible('gebruikersonderhoud.afvoerWC') && (
        <div className="form-group">
          <label>Afvoer WC</label>
          <Toggle
            value={session.gebruikersonderhoud?.afvoerWC}
            onChange={(val) => updateField('gebruikersonderhoud.afvoerWC', val)}
            options={['niet oke', 'oke']}
          />
        </div>
      )}

      {isStep4FieldVisible('gebruikersonderhoud.afvoerOpmerking') && (
        <div className="form-group">
          <label>Opmerking afvoer</label>
          <p style={{ fontSize: '0.85rem', color: '#7f8c8d', margin: '0.25rem 0 0.5rem 0' }}>
            Bijv. luchtjes, traag wegloop, gorgelende geluiden.
          </p>
          <textarea
            value={session.gebruikersonderhoud?.afvoerOpmerking || ''}
            onChange={(e) => updateField('gebruikersonderhoud.afvoerOpmerking', e.target.value)}
            className="form-input"
            rows="2"
            placeholder="Bijv. douche-afvoer luchtjes, gootsteen traag..."
          />
        </div>
      )}
      </>
      )}
    </div>
  );
};

// Gebruikersonderhoud Step 4: Afvoer & Foto's
const GebruikersonderhoudAfvoerFotosStep = ({ session, updateField, token, toast, stepConfigs }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(null); // Which photo number is being uploaded

  // Initialize default values for toggle fields
  useEffect(() => {
    const afvoerFields = ['afvoerBadkamer', 'afvoerKeuken', 'afvoerWC'];
    afvoerFields.forEach(field => {
      if (session.gebruikersonderhoud?.[field] === undefined) {
        updateField(`gebruikersonderhoud.${field}`, 'oke');
      }
    });
  }, []);

  // Helper function to check if a field is visible
  const isFieldVisible = (fieldPath) => {
    const stepConfig = stepConfigs?.find(config => config.stepId === 'gebruikersonderhoud4');
    if (!stepConfig || !stepConfig.fields || stepConfig.fields.length === 0) {
      return true; // Default to visible if no config
    }
    const field = stepConfig.fields.find(f => f.fieldPath === fieldPath);
    if (!field) {
      return true; // Default to visible if field not in config
    }
    return field.visible !== false;
  };

  const handlePhotoUpload = async (photoNumber, file) => {
    if (!file) return;

    setUploading(true);
    setUploadingPhoto(photoNumber);

    const formData = new FormData();
    formData.append('photo', file);
    formData.append('huisnummer', session.huisnummer);
    formData.append('location', 'gebruikersonderhoud');
    formData.append('detail', `foto${photoNumber}`);

    try {
      const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:6001';
      const response = await fetch(`${backendUrl}/api/inspections/session/${token}/photo`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) throw new Error('Upload failed');

      const data = await response.json();

      // Store the filename/URL in the foto field
      const photoUrl = `${backendUrl}/uploads/inspections/${data.filename}`;
      updateField(`gebruikersonderhoud.foto${photoNumber}`, photoUrl);

      toast.success(`Foto ${photoNumber} succesvol geüpload!`);
    } catch (err) {
      toast.error('Foto upload mislukt: ' + err.message);
    } finally {
      setUploading(false);
      setUploadingPhoto(null);
    }
  };

  return (
    <div className="step-content">
      <h2>Gebruikersonderhoud: Afvoer & Foto's</h2>

      <h3 style={{ marginTop: '1.5rem', marginBottom: '1rem', color: '#2c3e50' }}>Afvoer</h3>

      {isFieldVisible('gebruikersonderhoud.afvoerBadkamer') && (
        <div className="form-group">
          <label>Afvoer badkamer</label>
          <Toggle
            value={session.gebruikersonderhoud?.afvoerBadkamer}
            onChange={(val) => updateField('gebruikersonderhoud.afvoerBadkamer', val)}
            options={['niet oke', 'oke']}
          />
        </div>
      )}

      {isFieldVisible('gebruikersonderhoud.afvoerKeuken') && (
        <div className="form-group">
          <label>Afvoer keuken</label>
          <Toggle
            value={session.gebruikersonderhoud?.afvoerKeuken}
            onChange={(val) => updateField('gebruikersonderhoud.afvoerKeuken', val)}
            options={['niet oke', 'oke']}
          />
        </div>
      )}

      {isFieldVisible('gebruikersonderhoud.afvoerWC') && (
        <div className="form-group">
          <label>Afvoer WC</label>
          <Toggle
            value={session.gebruikersonderhoud?.afvoerWC}
            onChange={(val) => updateField('gebruikersonderhoud.afvoerWC', val)}
            options={['niet oke', 'oke']}
          />
        </div>
      )}

      {isFieldVisible('gebruikersonderhoud.afvoerOpmerking') && (
        <div className="form-group">
          <label>Opmerking afvoer</label>
          <textarea
            value={session.gebruikersonderhoud?.afvoerOpmerking || ''}
            onChange={(e) => updateField('gebruikersonderhoud.afvoerOpmerking', e.target.value)}
            className="form-input"
            rows="2"
            placeholder="Bijv. luchtjes, geluiden, etc."
          />
        </div>
      )}

      <h3 style={{ marginTop: '2rem', marginBottom: '1rem', color: '#2c3e50' }}>Extra Foto's</h3>
      <p style={{ fontSize: '0.9rem', color: '#7f8c8d', marginBottom: '1rem' }}>
        Upload extra foto's van relevante zaken in uw woning.
      </p>

      {[1, 2, 3, 4].map((photoNum) => (
        <div key={photoNum} style={{ marginBottom: '2rem', padding: '1rem', background: '#f8f9fa', borderRadius: '8px' }}>
          <h4 style={{ marginBottom: '1rem', color: '#2c3e50' }}>Foto {photoNum}</h4>

          <div className="form-group">
            <label>Upload Foto {photoNum}</label>
            <input
              type="file"
              accept="image/*"
              capture="environment"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) handlePhotoUpload(photoNum, file);
                e.target.value = '';
              }}
              style={{ display: 'none' }}
              id={`foto${photoNum}-input`}
              disabled={uploading}
            />
            <button
              type="button"
              onClick={() => document.getElementById(`foto${photoNum}-input`).click()}
              disabled={uploading}
              style={{
                width: '100%',
                padding: '1rem',
                background: session.gebruikersonderhoud?.[`foto${photoNum}`]
                  ? 'linear-gradient(135deg, #27ae60 0%, #229954 100%)'
                  : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: uploading ? 'not-allowed' : 'pointer',
                boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)'
              }}
            >
              {uploading && uploadingPhoto === photoNum
                ? 'Uploaden...'
                : session.gebruikersonderhoud?.[`foto${photoNum}`]
                  ? '✓ Foto geüpload - Klik om te vervangen'
                  : `Maak foto ${photoNum}`}
            </button>
          </div>

          {session.gebruikersonderhoud?.[`foto${photoNum}`] && (
            <div style={{ marginTop: '1rem' }}>
              <img
                src={session.gebruikersonderhoud[`foto${photoNum}`]}
                alt={`Foto ${photoNum}`}
                style={{
                  width: '100%',
                  maxHeight: '200px',
                  objectFit: 'contain',
                  borderRadius: '8px',
                  border: '2px solid #27ae60'
                }}
              />
            </div>
          )}

          <div className="form-group" style={{ marginTop: '1rem' }}>
            <label>Opmerking foto {photoNum}</label>
            <textarea
              value={session.gebruikersonderhoud?.[`foto${photoNum}Opmerking`] || ''}
              onChange={(e) => updateField(`gebruikersonderhoud.foto${photoNum}Opmerking`, e.target.value)}
              className="form-input"
              rows="2"
              placeholder={`Beschrijf wat er op foto ${photoNum} te zien is...`}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

// Gebruikersonderhoud Step 5: Wensen & opmerkingen
// Deze stap is samengevoegd: aanpassingen + wensen, foto's (uit verborgen stap 4),
// elektrische groepen (uit stap 3), en algemene vragen (uit verborgen algemeen-stap).
const GebruikersonderhoudAanpassingenStep = ({ session, updateField, token, stepConfigs, toast }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(null);
  const [docPreview, setDocPreview] = useState(null);
  const [docFile, setDocFile] = useState(null);

  // Helpers: visibility checks per oorspronkelijke stap-config (zo blijft vragenbeheer werken).
  const visibleIn = (stepId, fieldPath, fieldId) => {
    const stepConfig = stepConfigs?.find(c => c.stepId === stepId);
    if (!stepConfig || !stepConfig.fields || stepConfig.fields.length === 0) return true;
    const field = stepConfig.fields.find(f =>
      (fieldPath && f.fieldPath === fieldPath) || (fieldId && f.fieldId === fieldId)
    );
    if (!field) return true;
    return field.visible !== false;
  };
  const isFieldVisible = (fieldPath) => visibleIn('gebruikersonderhoud5', fieldPath);
  const isStep4FieldVisible = (fieldPath) => visibleIn('gebruikersonderhoud4', fieldPath);
  const isStep3FieldVisible = (fieldPath) => visibleIn('gebruikersonderhoud3', fieldPath);
  const isAlgemeenFieldVisible = (fieldId) => visibleIn('algemeen', null, fieldId);

  const handlePhotoUpload = async (photoNumber, file) => {
    if (!file) return;
    setUploading(true);
    setUploadingPhoto(photoNumber);
    const formData = new FormData();
    formData.append('photo', file);
    formData.append('huisnummer', session.huisnummer);
    formData.append('location', 'gebruikersonderhoud');
    formData.append('detail', `foto${photoNumber}`);
    try {
      const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:6001';
      const response = await fetch(`${backendUrl}/api/inspections/session/${token}/photo`, {
        method: 'POST',
        body: formData
      });
      if (!response.ok) throw new Error('Upload failed');
      const data = await response.json();
      const photoUrl = `${backendUrl}/uploads/inspections/${data.filename}`;
      updateField(`gebruikersonderhoud.foto${photoNumber}`, photoUrl);
      toast.success(`Foto ${photoNumber} succesvol geüpload!`);
    } catch (err) {
      toast.error('Foto upload mislukt: ' + err.message);
    } finally {
      setUploading(false);
      setUploadingPhoto(null);
    }
  };

  const handleDocSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => { setDocPreview(reader.result); setDocFile(file); };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const confirmDoc = async () => {
    if (!docFile) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('photo', docFile);
    formData.append('huisnummer', session.huisnummer);
    formData.append('location', 'algemeen');
    formData.append('detail', 'document');
    try {
      const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:6001';
      const response = await fetch(`${backendUrl}/api/inspections/session/${token}/photo`, {
        method: 'POST',
        body: formData
      });
      if (!response.ok) throw new Error('Upload failed');
      const data = await response.json();
      const currentDocs = session.algemeen?.extraDocumenten || [];
      updateField('algemeen.extraDocumenten', [...currentDocs, {
        filename: data.filename,
        url: data.url || `${backendUrl}/uploads/inspections/${data.filename}`
      }]);
      setDocPreview(null);
      setDocFile(null);
    } catch (err) {
      toast.error('Document upload mislukt: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const cancelDoc = () => { setDocPreview(null); setDocFile(null); };

  const deleteDocument = (index) => {
    const currentDocs = session.algemeen?.extraDocumenten || [];
    updateField('algemeen.extraDocumenten', currentDocs.filter((_, idx) => idx !== index));
  };

  return (
    <div className="step-content">
      <h2>Opmerkingen & foto's</h2>
      <p className="step-description">Tot slot — ruimte voor eventuele gebreken, opmerkingen en extra foto's of documenten.</p>

      {isFieldVisible('gebruikersonderhoud.aanpassing1') && (
        <div className="form-group">
          <label>Aanpassing 1</label>
          <textarea
            value={session.gebruikersonderhoud?.aanpassing1 || ''}
            onChange={(e) => updateField('gebruikersonderhoud.aanpassing1', e.target.value)}
            className="form-input"
            rows="2"
            placeholder="Beschrijf aanpassingen die zijn gedaan..."
          />
        </div>
      )}

      {isFieldVisible('gebruikersonderhoud.aanpassing1Opmerking') && (
        <div className="form-group">
          <label>Opmerking 1</label>
          <textarea
            value={session.gebruikersonderhoud?.aanpassing1Opmerking || ''}
            onChange={(e) => updateField('gebruikersonderhoud.aanpassing1Opmerking', e.target.value)}
            className="form-input"
            rows="2"
          />
        </div>
      )}

      {isFieldVisible('gebruikersonderhoud.aanpassing2') && (
        <div className="form-group">
          <label>Aanpassing 2</label>
          <textarea
            value={session.gebruikersonderhoud?.aanpassing2 || ''}
            onChange={(e) => updateField('gebruikersonderhoud.aanpassing2', e.target.value)}
            className="form-input"
            rows="2"
          />
        </div>
      )}

      {isFieldVisible('gebruikersonderhoud.aanpassing2Opmerking') && (
        <div className="form-group">
          <label>Opmerking 2</label>
          <textarea
            value={session.gebruikersonderhoud?.aanpassing2Opmerking || ''}
            onChange={(e) => updateField('gebruikersonderhoud.aanpassing2Opmerking', e.target.value)}
            className="form-input"
            rows="2"
          />
        </div>
      )}

      {isFieldVisible('gebruikersonderhoud.wens') && (
        <div className="form-group">
          <label>Wens</label>
          <textarea
            value={session.gebruikersonderhoud?.wens || ''}
            onChange={(e) => updateField('gebruikersonderhoud.wens', e.target.value)}
            className="form-input"
            rows="3"
            placeholder="Eventuele wensen mbt onderhoud of aanpassingen..."
          />
        </div>
      )}

      {isFieldVisible('gebruikersonderhoud.wensOpmerking') && (
        <div className="form-group">
          <label>Opmerking 3</label>
          <textarea
            value={session.gebruikersonderhoud?.wensOpmerking || ''}
            onChange={(e) => updateField('gebruikersonderhoud.wensOpmerking', e.target.value)}
            className="form-input"
            rows="2"
          />
        </div>
      )}

      {isFieldVisible('gebruikersonderhoud.opmerking1') && (
        <div className="form-group">
          <label>Opmerking 1</label>
          <textarea
            value={session.gebruikersonderhoud?.opmerking1 || ''}
            onChange={(e) => updateField('gebruikersonderhoud.opmerking1', e.target.value)}
            className="form-input"
            rows="2"
          />
        </div>
      )}

      {isFieldVisible('gebruikersonderhoud.toelichting1') && (
        <div className="form-group">
          <label>Toelichting 1</label>
          <textarea
            value={session.gebruikersonderhoud?.toelichting1 || ''}
            onChange={(e) => updateField('gebruikersonderhoud.toelichting1', e.target.value)}
            className="form-input"
            rows="2"
          />
        </div>
      )}

      {isFieldVisible('gebruikersonderhoud.opmerking2') && (
        <div className="form-group">
          <label>Opmerking 2</label>
          <textarea
            value={session.gebruikersonderhoud?.opmerking2 || ''}
            onChange={(e) => updateField('gebruikersonderhoud.opmerking2', e.target.value)}
            className="form-input"
            rows="2"
          />
        </div>
      )}

      {isFieldVisible('gebruikersonderhoud.toelichting2') && (
        <div className="form-group">
          <label>Toelichting 2</label>
          <textarea
            value={session.gebruikersonderhoud?.toelichting2 || ''}
            onChange={(e) => updateField('gebruikersonderhoud.toelichting2', e.target.value)}
            className="form-input"
            rows="2"
          />
        </div>
      )}

      {isFieldVisible('gebruikersonderhoud.afspraak1') && (
        <div className="form-group">
          <label>Afspraak 1</label>
          <textarea
            value={session.gebruikersonderhoud?.afspraak1 || ''}
            onChange={(e) => updateField('gebruikersonderhoud.afspraak1', e.target.value)}
            className="form-input"
            rows="2"
          />
        </div>
      )}

      {isFieldVisible('gebruikersonderhoud.actiehouder1') && (
        <div className="form-group">
          <label>Actiehouder 1</label>
          <input
            type="text"
            value={session.gebruikersonderhoud?.actiehouder1 || ''}
            onChange={(e) => updateField('gebruikersonderhoud.actiehouder1', e.target.value)}
            className="form-input"
            placeholder="Bijv. Bestuur, Bewoner, VVE"
          />
        </div>
      )}

      {isFieldVisible('gebruikersonderhoud.afspraak2') && (
        <div className="form-group">
          <label>Afspraak 2</label>
          <textarea
            value={session.gebruikersonderhoud?.afspraak2 || ''}
            onChange={(e) => updateField('gebruikersonderhoud.afspraak2', e.target.value)}
            className="form-input"
            rows="2"
          />
        </div>
      )}

      {isFieldVisible('gebruikersonderhoud.actiehouder2') && (
        <div className="form-group">
          <label>Actiehouder 2</label>
          <input
            type="text"
            value={session.gebruikersonderhoud?.actiehouder2 || ''}
            onChange={(e) => updateField('gebruikersonderhoud.actiehouder2', e.target.value)}
            className="form-input"
          />
        </div>
      )}

      {isFieldVisible('gebruikersonderhoud.afspraak3') && (
        <div className="form-group">
          <label>Afspraak 3</label>
          <textarea
            value={session.gebruikersonderhoud?.afspraak3 || ''}
            onChange={(e) => updateField('gebruikersonderhoud.afspraak3', e.target.value)}
            className="form-input"
            rows="2"
          />
        </div>
      )}

      {isFieldVisible('gebruikersonderhoud.actiehouder3') && (
        <div className="form-group">
          <label>Actiehouder 3</label>
          <input
            type="text"
            value={session.gebruikersonderhoud?.actiehouder3 || ''}
            onChange={(e) => updateField('gebruikersonderhoud.actiehouder3', e.target.value)}
            className="form-input"
          />
        </div>
      )}

      {isFieldVisible('gebruikersonderhoud.afspraak4') && (
        <div className="form-group">
          <label>Afspraak 4</label>
          <textarea
            value={session.gebruikersonderhoud?.afspraak4 || ''}
            onChange={(e) => updateField('gebruikersonderhoud.afspraak4', e.target.value)}
            className="form-input"
            rows="2"
          />
        </div>
      )}

      {isFieldVisible('gebruikersonderhoud.actiehouder4') && (
        <div className="form-group">
          <label>Actiehouder 4</label>
          <input
            type="text"
            value={session.gebruikersonderhoud?.actiehouder4 || ''}
            onChange={(e) => updateField('gebruikersonderhoud.actiehouder4', e.target.value)}
            className="form-input"
          />
        </div>
      )}

      {isFieldVisible('gebruikersonderhoud.afspraak5') && (
        <div className="form-group">
          <label>Afspraak 5</label>
          <textarea
            value={session.gebruikersonderhoud?.afspraak5 || ''}
            onChange={(e) => updateField('gebruikersonderhoud.afspraak5', e.target.value)}
            className="form-input"
            rows="2"
          />
        </div>
      )}

      {isFieldVisible('gebruikersonderhoud.actiehouder5') && (
        <div className="form-group">
          <label>Actiehouder 5</label>
          <input
            type="text"
            value={session.gebruikersonderhoud?.actiehouder5 || ''}
            onChange={(e) => updateField('gebruikersonderhoud.actiehouder5', e.target.value)}
            className="form-input"
          />
        </div>
      )}

      {/* Aanpassingen aan algemene gedeeltes (uit verborgen algemeen-stap) */}
      {isAlgemeenFieldVisible('algemeen_aanpassingenAlgemeneGedeelte') && (
        <div className="form-group" style={{ marginTop: '2rem' }}>
          <label>Aanpassingen aan algemene gedeeltes</label>
          <p style={{ fontSize: '0.9rem', color: '#7f8c8d', marginBottom: '0.5rem' }}>
            Heeft u aanpassingen gedaan aan algemene gedeeltes (gang, tussendeur, gevel)?
          </p>
          <textarea
            value={session.algemeen?.aanpassingenAlgemeneGedeelte || ''}
            onChange={(e) => updateField('algemeen.aanpassingenAlgemeneGedeelte', e.target.value)}
            className="form-input"
            rows="3"
            placeholder="Beschrijf eventuele aanpassingen..."
          />
        </div>
      )}

      {/* Meterkast & elektriciteit is verplaatst naar Installaties (gebruikersonderhoud1) */}

      {/* Gebreken & extra opmerkingen */}
      {isAlgemeenFieldVisible('algemeen_eventueleGebreken') && (
        <div className="form-group" style={{ marginTop: '2rem' }}>
          <label>Eventuele gebreken</label>
          <p style={{ fontSize: '0.9rem', color: '#7f8c8d', marginBottom: '0.5rem' }}>
            Zijn er nog andere gebreken of aandachtspunten die niet eerder genoemd zijn?
          </p>
          <textarea
            value={session.algemeen?.eventueleGebreken || ''}
            onChange={(e) => updateField('algemeen.eventueleGebreken', e.target.value)}
            className="form-input"
            rows="4"
            placeholder="Beschrijf eventuele gebreken..."
          />
        </div>
      )}

      {isAlgemeenFieldVisible('algemeen_extraOpmerkingen') && (
        <div className="form-group">
          <label>Extra opmerkingen</label>
          <textarea
            value={session.algemeen?.extraOpmerkingen || ''}
            onChange={(e) => updateField('algemeen.extraOpmerkingen', e.target.value)}
            className="form-input"
            rows="4"
            placeholder="Ruimte voor overige opmerkingen..."
          />
        </div>
      )}

      {/* Extra foto's (uit verborgen stap 4) */}
      <h3 style={{ marginTop: '2rem', marginBottom: '1rem', color: '#2c3e50' }}>Extra foto's</h3>
      <p style={{ fontSize: '0.9rem', color: '#7f8c8d', marginBottom: '1rem' }}>
        Maak hier eventueel foto's van zaken die u nog wilt laten zien.
      </p>

      {[1, 2, 3, 4].map((photoNum) => {
        if (!isStep4FieldVisible(`gebruikersonderhoud.foto${photoNum}`)) return null;
        return (
          <div key={photoNum} style={{ marginBottom: '2rem', padding: '1rem', background: '#f8f9fa', borderRadius: '8px' }}>
            <h4 style={{ marginBottom: '1rem', color: '#2c3e50' }}>Foto {photoNum}</h4>
            <div className="form-group">
              <input
                type="file"
                accept="image/*"
                capture="environment"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) handlePhotoUpload(photoNum, file);
                  e.target.value = '';
                }}
                style={{ display: 'none' }}
                id={`s5-foto${photoNum}-input`}
                disabled={uploading}
              />
              <button
                type="button"
                onClick={() => document.getElementById(`s5-foto${photoNum}-input`).click()}
                disabled={uploading}
                style={{
                  width: '100%',
                  padding: '1rem',
                  background: session.gebruikersonderhoud?.[`foto${photoNum}`]
                    ? 'linear-gradient(135deg, #27ae60 0%, #229954 100%)'
                    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: uploading ? 'not-allowed' : 'pointer',
                  boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)'
                }}
              >
                {uploading && uploadingPhoto === photoNum
                  ? 'Uploaden...'
                  : session.gebruikersonderhoud?.[`foto${photoNum}`]
                    ? '✓ Foto geüpload - Klik om te vervangen'
                    : `Maak foto ${photoNum}`}
              </button>
            </div>
            {session.gebruikersonderhoud?.[`foto${photoNum}`] && (
              <div style={{ marginTop: '1rem' }}>
                <img
                  src={session.gebruikersonderhoud[`foto${photoNum}`]}
                  alt={`Foto ${photoNum}`}
                  style={{ width: '100%', maxHeight: '200px', objectFit: 'contain', borderRadius: '8px', border: '2px solid #27ae60' }}
                />
              </div>
            )}
            {isStep4FieldVisible(`gebruikersonderhoud.foto${photoNum}Opmerking`) && (
              <div className="form-group" style={{ marginTop: '1rem' }}>
                <label>Opmerking foto {photoNum}</label>
                <textarea
                  value={session.gebruikersonderhoud?.[`foto${photoNum}Opmerking`] || ''}
                  onChange={(e) => updateField(`gebruikersonderhoud.foto${photoNum}Opmerking`, e.target.value)}
                  className="form-input"
                  rows="2"
                  placeholder={`Beschrijf wat er op foto ${photoNum} te zien is...`}
                />
              </div>
            )}
          </div>
        );
      })}

      {/* Extra documenten (uit verborgen algemeen-stap) */}
      {isAlgemeenFieldVisible('algemeen_extraDocumenten') && (
        <>
          <h3 style={{ marginTop: '2rem', marginBottom: '1rem', color: '#2c3e50' }}>Extra documenten</h3>
          <p style={{ fontSize: '0.9rem', color: '#7f8c8d', marginBottom: '1rem' }}>
            Eventuele extra documenten of foto's (offertes, garantiebewijzen, rapporten).
          </p>

          {!docPreview && (
            <div className="form-group">
              <input
                type="file"
                accept="image/*,application/pdf"
                onChange={handleDocSelect}
                style={{ display: 'none' }}
                id="s5-document-input"
                disabled={uploading}
              />
              <button
                type="button"
                onClick={() => document.getElementById('s5-document-input').click()}
                disabled={uploading}
                style={{
                  width: '100%',
                  padding: '1rem',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: uploading ? 'not-allowed' : 'pointer',
                  boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)'
                }}
              >
                Document toevoegen
              </button>
            </div>
          )}

          {docPreview && (
            <div style={{ marginTop: '1rem', padding: '1rem', background: '#f8f9fa', borderRadius: '8px' }}>
              <img src={docPreview} alt="Preview"
                style={{ width: '100%', maxHeight: '250px', objectFit: 'contain', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', marginBottom: '1rem' }}
              />
              <div style={{ display: 'flex', gap: '1rem' }}>
                <button onClick={confirmDoc} disabled={uploading}
                  style={{ flex: 1, padding: '0.75rem', background: '#27ae60', color: 'white', border: 'none', borderRadius: '8px', fontSize: '1rem', fontWeight: '600', cursor: uploading ? 'not-allowed' : 'pointer' }}>
                  {uploading ? 'Uploaden...' : 'Bevestigen'}
                </button>
                <button onClick={cancelDoc} disabled={uploading}
                  style={{ flex: 1, padding: '0.75rem', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '8px', fontSize: '1rem', fontWeight: '600', cursor: uploading ? 'not-allowed' : 'pointer' }}>
                  Verwijderen
                </button>
              </div>
            </div>
          )}

          {session.algemeen?.extraDocumenten && session.algemeen.extraDocumenten.length > 0 && (
            <div style={{ marginTop: '1.5rem' }}>
              <h4 style={{ marginBottom: '1rem', color: '#2c3e50' }}>
                Geüploade documenten ({session.algemeen.extraDocumenten.length}):
              </h4>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                {session.algemeen.extraDocumenten.map((doc, idx) => {
                  const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:6001';
                  const docUrl = typeof doc === 'string'
                    ? `${backendUrl}/uploads/inspections/${doc}`
                    : doc.url || `${backendUrl}/uploads/inspections/${doc.filename}`;
                  const docName = typeof doc === 'string' ? doc : doc.filename;
                  return (
                    <li key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem', background: '#f8f9fa', borderRadius: '4px', marginBottom: '0.5rem' }}>
                      <a href={docUrl} target="_blank" rel="noopener noreferrer">{docName}</a>
                      <button onClick={() => deleteDocument(idx)}
                        style={{ background: '#e74c3c', color: 'white', border: 'none', borderRadius: '4px', padding: '0.25rem 0.5rem', cursor: 'pointer' }}>
                        Verwijderen
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const ReviewStep = ({ session }) => (
  <div className="step-content">
    <h2>Controleren & Indienen</h2>
    <p>Controleer uw gegevens voordat u indient.</p>

    <div className="info-box">
      <h3>✓ Wat gebeurt er na indienen?</h3>
      <ul>
        <li>Uw gegevens worden opgeslagen</li>
        <li>Foto's worden geüpload naar Google Drive</li>
        <li>Het bestuur ontvangt een notificatie</li>
        <li>U ontvangt een bevestiging per email</li>
      </ul>
    </div>

    <div className="warning-box">
      <p><strong>Let op:</strong> Na indienen kunt u de opnamestaat niet meer wijzigen.</p>
    </div>
  </div>
);

export default InspectionWizard;
